/* =========================================================================
   Stack — standalone product section behaviour
   -------------------------------------------------------------------------
   One self-contained controller per <stack-product> section orchestrates every
   stack-* block through a single custom event: `stack:variant-change`.

   Architecture
     <stack-product>  ............ the controller / source of truth.
       Holds the product's variants + state (selected variant, quantity).
       Children CALL its methods; it recomputes and EMITS `stack:variant-change`.
     Input blocks   ............... <stack-variant-picker>, <stack-bundle>,
       <stack-quantity>, the <stack-atc> stepper → call controller methods.
     Display blocks ............... <stack-price>, <stack-title>, <stack-gallery>,
       <stack-atc> button → listen for `stack:variant-change` and update the DOM.

   The event detail is always: { variant, quantity, optionValues }.
   `variant` is null when the current option combination has no matching variant.

   Swiper (gallery + video carousel) is loaded once from a CDN on demand and the
   gallery degrades gracefully (see stack.css) until it is ready.

   No dependency on the host theme's JS, classes or globals.
   ========================================================================= */
(function () {
  'use strict';

  if (window.__StackProductLoaded) return;
  window.__StackProductLoaded = true;

  /* ---------------------------------------------------------------------
     Swiper loader (CDN, memoised)
     --------------------------------------------------------------------- */
  var SWIPER_VERSION = '11';
  var SWIPER_CSS = 'https://unpkg.com/swiper@' + SWIPER_VERSION + '/swiper-bundle.min.css';
  var SWIPER_JS = 'https://unpkg.com/swiper@' + SWIPER_VERSION + '/swiper-bundle.min.js';
  var swiperPromise = null;

  function loadSwiper() {
    if (window.Swiper) return Promise.resolve(window.Swiper);
    if (swiperPromise) return swiperPromise;
    swiperPromise = new Promise(function (resolve, reject) {
      if (!document.querySelector('link[data-stack-swiper]')) {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = SWIPER_CSS;
        link.setAttribute('data-stack-swiper', '');
        document.head.appendChild(link);
      }
      var existing = document.querySelector('script[data-stack-swiper]');
      if (existing) {
        existing.addEventListener('load', function () { resolve(window.Swiper); });
        existing.addEventListener('error', reject);
        return;
      }
      var s = document.createElement('script');
      s.src = SWIPER_JS;
      s.async = true;
      s.setAttribute('data-stack-swiper', '');
      s.addEventListener('load', function () { resolve(window.Swiper); });
      s.addEventListener('error', function () { reject(new Error('Stack: Swiper failed to load')); });
      document.head.appendChild(s);
    });
    return swiperPromise;
  }

  /* ---------------------------------------------------------------------
     Money formatting (Shopify-compatible, no theme dependency)
     --------------------------------------------------------------------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var formatString = format || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var rest = parts[1] ? decimal + parts[1] : '';
      return dollars + rest;
    }

    var match = formatString.match(placeholder);
    var value = '';
    switch (match ? match[1] : 'amount') {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      default: value = withDelimiters(cents, 2);
    }
    return formatString.replace(placeholder, value);
  }

  /* Net line total (in cents) for `qty` units under a Stack deal code
     ("p:40@5" percent / "a:10@5" fixed / "f:2@5" free units — see
     blocks/stack-bundle-tier.liquid). The math MUST mirror the discount
     Function exactly (percent = line gross minus the rounded-half-up
     discount; free = unit × paid units) so the storefront never displays
     a cent the checkout doesn't honour. */
  function bundleNet(unitCents, qty, deal) {
    var gross = unitCents * qty;
    if (!deal) return gross;
    var parts = String(deal).split(':');
    var val = parseFloat((parts[1] || '').split('@')[0]) || 0;
    if (val <= 0) return gross;
    if (parts[0] === 'p') return gross - Math.round((gross * Math.min(100, val)) / 100);
    if (parts[0] === 'a') return Math.max(0, gross - Math.round(val * 100));
    if (parts[0] === 'f') {
      var free = Math.min(Math.floor(val), qty - 1);
      return free > 0 ? unitCents * (qty - free) : gross;
    }
    return gross;
  }

  /* ---------------------------------------------------------------------
     Host-theme cart bridge (stack-theme-bridge.js loader + dispatcher)

     After any AJAX cart mutation (upsell instant add/remove, shop-the-look
     add) the HOST theme's cart UI — drawer, count bubble — must refresh, and
     every theme has its own event system. That per-theme knowledge lives in
     its OWN small asset, assets/stack-theme-bridge.js, so it can be updated
     weekly (new themes, customer requests) without redeploying this file.

     Contract (stable, transport-agnostic):
       stack.js  →  dispatches  stack:cart:update  (detail = /cart.js JSON)
                                stack:cart:open    (show the drawer)
       adapter   →  translates those into the current theme's own events/APIs.

     The adapter is injected once, as a sibling asset of this script (same
     Shopify CDN, no third-party origin) — skipped if window.StackTheme is
     already present (e.g. it was injected by another stack.js instance on
     the page). When Shopify's announced global cart event is finalised,
     only the adapter internals change; this file stays as-is.

     The legacy `cart:refresh` dispatches at each call site are kept — some
     themes (and the adapter's fallback) already listen for them.
     --------------------------------------------------------------------- */
  var STACK_SRC = (document.currentScript && document.currentScript.src) || '';

  function stackShopRoot() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  var stackThemeInjected = false;
  function loadStackTheme() {
    if (stackThemeInjected) return;
    stackThemeInjected = true;
    if (window.StackTheme) { stackLog('stack-theme-bridge.js already present, skipping injection'); return; }
    if (document.querySelector('script[data-stack-theme-bridge]')) { stackLog('stack-theme-bridge.js script tag already present, skipping injection'); return; }
    var src = STACK_SRC || (function () {
      var el = document.querySelector('script[src*="stack.js"]');
      return (el && el.src) || '';
    })();
    if (!src) { stackLogErr('could not determine stack.js\'s own URL — cannot inject stack-theme-bridge.js'); return; }
    // Sibling asset on the same CDN path; drop stack.js's version query.
    var adapter = src.replace(/stack\.js[^\/]*$/, 'stack-theme-bridge.js');
    if (adapter === src) { stackLogErr('stack.js URL did not match the expected pattern, cannot derive the bridge URL:', src); return; }
    var s = document.createElement('script');
    s.src = adapter;
    s.defer = true;
    s.setAttribute('data-stack-theme-bridge', '');
    s.addEventListener('load', function () { stackLog('stack-theme-bridge.js loaded from', adapter); });
    s.addEventListener('error', function () { stackLogErr('stack-theme-bridge.js FAILED to load from', adapter); });
    document.head.appendChild(s);
  }

  /* The built-in, ALWAYS-ON cart-UI refresh. This is the primary path — it
     does NOT depend on the separately-injected stack-theme-bridge.js loading
     (which, being a runtime-injected sibling asset, can fail to load, 404, or
     resolve to a stale copy from a different theme snapshot). The bridge is a
     SUPPLEMENT for non-standard themes (Dawn section-render, Ella, etc.).

     TWO HORIZON GENERATIONS, TWO DIFFERENT EVENTS — both are dispatched:

       Horizon 4.x+ (and every theme on Shopify's component system)
         `shopify:cart:lines-update`. Consumers read `.action` synchronously
         and await `.promise`. Verified against theme v4.1.1: cart-icon.js,
         header-actions.js, component-cart-items.js, cart-drawer.js.

       Horizon 1.x–3.x
         `cart:update` (ThemeEvents.cartUpdate) — a different name AND a
         different payload: consumers read `detail.data.itemCount` /
         `detail.data.sections` synchronously, no promise. Verified against
         Shopify/horizon @ d534fca (v3.4.0).

     The two are mutually inert: 4.x has zero references to `cart:update`, and
     ≤3.x has zero references to the standard event. So exactly one generation
     reacts and nothing double-renders. Without the legacy event the cart-icon
     bubble on ≤3.x never moves — the shopper gets no feedback that the add
     worked, which is invisible when the drawer is off.

     `action` MUST be truthful: on 4.x it is the ONLY thing gating
     cart-drawer-component's auto-open, so it is what makes a silent refresh
     silent. ≤3.x has no equivalent — `CartAddEvent.eventName ===
     ThemeEvents.cartUpdate`, i.e. EVERY `cart:update` auto-opens the drawer.
     The only lever there is the `auto-open` attribute, so a silent refresh
     pulls it for the duration of the dispatch and restores it immediately
     (every listener in both generations runs synchronously inside
     dispatchEvent, so the window is closed before anything else can observe
     it). That also covers 4.x, which reads the attribute synchronously.

     @param {any} cart        the full /cart.js JSON (may be null if it failed)
     @param {string} action   'add' | 'update' | 'remove' — never lie
     @param {boolean} openDrawer  show the cart drawer afterwards */
  function stackBuiltinCartUI(cart, action, openDrawer) {
    // Update a Dawn-compatible cart drawer without adding the product twice.
    var drawer = document.querySelector('cart-drawer');
    if (openDrawer && drawer && typeof drawer.renderContents === 'function') {
      fetch(stackShopRoot() + '?sections=cart-drawer,cart-icon-bubble', {
        headers: { Accept: 'application/json' }, cache: 'no-store'
      }).then(function (response) {
        if (!response.ok) throw new Error('Cart sections HTTP ' + response.status);
        return response.json();
      }).then(function (sections) {
        if (!sections['cart-drawer'] || !sections['cart-icon-bubble']) {
          throw new Error('Missing cart sections');
        }
        if (cart && cart.item_count > 0) drawer.classList.remove('is-empty');
        drawer.renderContents({ sections: sections,
          id: cart && cart.items && cart.items[0] ? cart.items[0].id : null });
      })['catch'](function (error) { stackLogErr('Drawer refresh failed', error); });
      return;
    }
    var itemCount = cart && cart.item_count != null ? cart.item_count : null;
    // Shared payload. `didError` is read by 4.x's cart-drawer-component; the
    // partial `cart` covers the two 4.x consumers that prefer it over
    // `detail` (cart-icon's bubble, header-actions' screen-reader
    // announcement) — both only ever read `totalQuantity`.
    var data = {
      items: (cart && cart.items) || [],
      itemCount: itemCount,
      source: 'stack',
      didError: false
    };

    // Only Horizon-family themes get the legacy event — `cart:update` is a
    // generic enough name that firing it at an unrelated theme could trigger
    // an unrelated handler. These custom-element names are Horizon's.
    var horizonFamily = document.querySelector('cart-icon, cart-drawer-component, cart-items-component');

    var restore = [];
    if (!openDrawer) {
      var autoOpen = document.querySelectorAll('cart-drawer-component[auto-open]');
      for (var i = 0; i < autoOpen.length; i++) {
        autoOpen[i].removeAttribute('auto-open');
        restore.push(autoOpen[i]);
      }
      if (restore.length) stackLog('built-in: suppressed auto-open for a silent cart refresh');
    }

    try {
      // 1) Horizon 4.x+ / component-system themes. With no `sections` in the
      //    resolved detail, cart-items-component refetches & re-renders.
      try {
        var evt = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
        evt.action = action || 'add';
        evt.context = 'product';
        evt.promise = Promise.resolve({
          cart: itemCount != null ? { totalQuantity: itemCount } : null,
          detail: data
        });
        document.dispatchEvent(evt);
        stackLog('built-in: dispatched shopify:cart:lines-update (' + evt.action + ')');
      } catch (e) { stackLogErr('built-in: could not dispatch standard cart event', e); }

      // 2) Horizon 1.x–3.x. Inert on 4.x — nothing there listens for this name.
      if (horizonFamily) {
        try {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: cart || null, sourceId: 'stack', data: data }
          }));
          stackLog('built-in: dispatched cart:update (legacy Horizon)');
        } catch (e) { stackLogErr('built-in: could not dispatch legacy cart event', e); }
      }
    } finally {
      for (var j = 0; j < restore.length; j++) {
        var node = restore[j];
        // A listener may have morphed the drawer section out from under us.
        // Put the setting back on whatever is live now — a silent refresh must
        // never permanently disable the merchant's auto-open.
        if (!document.contains(node)) node = document.querySelector('cart-drawer-component') || node;
        node.setAttribute('auto-open', '');
      }
    }

    // 3) Open the drawer — ONLY if the theme did not already do it itself.
    //    Both generations auto-open when the merchant enabled that setting, and
    //    re-opening an open drawer is not free: Horizon 4.x's
    //    ThemeDrawer.open() routes to #bringToFront(), which in modal mode
    //    does close() + showModal() and replays the slide-in animation.
    if (!openDrawer) return;
    stackOpenCartDrawer();
  }

  /* Open the host theme's cart drawer if one is present and not already open.
     4.x: an ancestor <theme-drawer id="cart-drawer"> owns the dialog lifecycle.
     ≤3.x: <cart-drawer-component> IS the dialog host (it extends
     DialogComponent and exposes open()). */
  function stackOpenCartDrawer() {
    var themeDrawer = document.querySelector('theme-drawer#cart-drawer');
    if (themeDrawer) {
      // ThemeDrawer.isOpen is `hasAttribute('open')`.
      if (themeDrawer.hasAttribute('open')) { stackLog('built-in: cart drawer already open'); return; }
      whenElementUpgraded('theme-drawer', function () {
        if (themeDrawer.hasAttribute('open')) return;
        if (typeof themeDrawer.open === 'function') themeDrawer.open();
      });
      return;
    }
    var legacyDrawer = document.querySelector('cart-drawer-component');
    if (!legacyDrawer) { stackLog('built-in: no cart drawer on this page'); return; }
    if (legacyDrawer.querySelector('dialog[open]')) { stackLog('built-in: cart drawer already open'); return; }
    whenElementUpgraded('cart-drawer-component', function () {
      if (legacyDrawer.querySelector('dialog[open]')) return;
      if (typeof legacyDrawer.open === 'function') legacyDrawer.open();
      else stackLog('built-in: cart-drawer-component has no open() — leaving it alone');
    });
  }

  /* Run `fn` once a custom element definition exists. A not-yet-upgraded
     element has no methods, and forcing an `open` attribute instead is a trap:
     Horizon reads [open] as "already open", so the drawer would report open
     while its <dialog> was never shown. */
  function whenElementUpgraded(tagName, fn) {
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined(tagName).then(fn, function () {});
    } else { fn(); }
  }

  /* Fetch the authoritative cart once, then notify the bridge. Passing the
     full /cart.js JSON lets every theme adapter read item_count etc. without
     its own round-trip.

     @param {string} action  what actually happened — 'add' | 'update' |
       'remove'. Passing 'add' for a removal makes Horizon 4.x auto-open the
       cart drawer, so this must mirror the mutation that just ran.
     @param {boolean} openDrawer  show the cart drawer afterwards */
  function notifyCartUpdate(action, openDrawer) {
    function dispatch(cart) {
      // Standard path — always runs, no dependency on the bridge loading.
      stackBuiltinCartUI(cart, action, openDrawer);
      // Supplement: notify the theme bridge (if present) for non-standard
      // themes that DON'T react to either Horizon event (Dawn, Ella, …).
      // On Horizon the bridge deliberately no-ops — the built-in above owns
      // both generations' events and the drawer.
      document.dispatchEvent(new CustomEvent('stack:cart:update', { bubbles: true, detail: cart || {} }));
      if (openDrawer) {
        document.dispatchEvent(new CustomEvent('stack:cart:open', { bubbles: true }));
      }
    }
    fetch(stackShopRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        stackLog('notifyCartUpdate: ' + action + (openDrawer ? ' + open drawer' : ' (silent)'), cart);
        dispatch(cart);
      })
      ['catch'](function (err) {
        stackLogErr('notifyCartUpdate: failed to fetch /cart.js —', err);
        dispatch(null);
      });
  }

  /* Shared /cart/add.js POST — every AJAX add in this file (main ATC, sticky
     ATC, upsell) goes through this one function. */
  function stackCartAdd(items) {
    stackLog('POST /cart/add.js', items);
    return fetch(stackShopRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (e) { stackLogErr('/cart/add.js failed:', res.status, e); throw e; });
      return res.json();
    });
  }

  /* Debug logging — silent by default. Set window.StackDebug = true in the
     console (before or after a click) and retry to see every step of the
     add-to-cart flow. Actual errors always print via console.error regardless
     of the flag — a failed add must never be silently swallowed. */
  function stackLog() {
    if (window.StackDebug && window.console) console.log.apply(console, ['[Stack]'].concat([].slice.call(arguments)));
  }
  function stackLogErr() {
    if (window.console) console.error.apply(console, ['[Stack]'].concat([].slice.call(arguments)));
  }

  /* small helper: safe customElements.define */
  function define(name, ctor) {
    if (!window.customElements || customElements.get(name)) return;
    customElements.define(name, ctor);
  }

  /* base for display/input blocks that need the controller + change events */
  function findController(el) {
    // `_host` is the fallback for elements that parked themselves on <body>
    // (see <stack-sticky-atc>) and can no longer reach the section by ancestry.
    return (el.closest && el.closest('stack-product')) || el._host || null;
  }

  /* =====================================================================
     <stack-product> — the section controller
     ===================================================================== */
  var StackProduct = (function () {
    function StackProduct() {
      return Reflect.construct(HTMLElement, [], StackProduct);
    }
    StackProduct.prototype = Object.create(HTMLElement.prototype);
    StackProduct.prototype.constructor = StackProduct;

    StackProduct.prototype.connectedCallback = function () {
      if (this._init) return;
      this._init = true;
      this.ready = false;
      this.variants = [];
      this.options = [];
      this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
      this.quantity = 1;
      this.currentVariant = null;
      this.selectedOptions = [];
      // True only when the shopper explicitly landed on / picked a variant:
      // a ?variant=… URL param on load, or a later selectOption/selectVariantId
      // call. Display blocks (e.g. the gallery) use this to avoid snapping to a
      // "default" variant's media on a fresh page load.
      this.explicitVariant = false;
      try {
        if (new URL(window.location.href).searchParams.get('variant')) {
          this.explicitVariant = true;
        }
      } catch (e) { /* no URL API → treat as non-explicit */ }

      var script = this.querySelector('script[data-stack-product-data]');
      if (script) {
        try {
          var data = JSON.parse(script.textContent);
          this.variants = data.variants || [];
          this.options = data.options || [];
          // A SOLD-OUT variant is only ever pre-selected when the shopper asked
          // for it by hand (?variant=…). Liquid's
          // selected_or_first_available_variant falls back to the FIRST variant
          // on a fully sold-out product, which would light that variant's swatch
          // up as "selected" next to a dead "Sold out" button. With nothing
          // available, nothing is selected — no variant, no option values.
          var server = data.selectedId ? this._byId(data.selectedId) : null;
          var selected =
            (server && (server.available || this.explicitVariant) ? server : null) ||
            this._firstAvailable() ||
            null;
          this.currentVariant = selected;
          this.selectedOptions = selected ? selected.options.slice() : [];
        } catch (e) {
          /* leave server-rendered markup untouched on bad data */
        }
      }

      this.ready = true;
      // Push an initial state so any child that missed the synchronous pull syncs.
      var self = this;
      requestAnimationFrame(function () { self._emit(); });

      this._wireCartSubmit();
    };

    /* Owns the ACTUAL add-to-cart request for every form in this section
       (the main stack-atc__form and the sticky stack-sticky__form both use
       the native Shopify {% form 'product' %} tag — no host theme listens
       for their submit, since the markup isn't theirs, so left alone a click
       does a real full-page POST and Shopify redirects to /cart by default).
       `submit` bubbles, so ONE listener here catches both forms — except the
       sticky bar once it has parked itself on <body> (outside this element):
       StackStickyAtc re-routes its submit to handleCartSubmit by hand. */
    StackProduct.prototype._wireCartSubmit = function () {
      var self = this;
      this.addEventListener('submit', function (e) { self.handleCartSubmit(e); });
    };

    StackProduct.prototype.handleCartSubmit = function (e) {
      var self = this;
      var form = e.target;
        if (!form || form.nodeName !== 'FORM') {
          stackLog('submit ignored — not a form element', e.target);
          return;
        }
        var idInput = form.querySelector('input[name="id"]');
        if (!idInput || !idInput.value) {
          stackLog('submit ignored — no input[name="id"] with a value found in', form);
          return;
        }
        e.preventDefault();
        // NB: form.getAttribute('id'), not form.id — a child <input name="id">
        // shadows the form's `id` property with the element itself.
        stackLog('intercepted submit on', form.getAttribute('id') || form.className);

        var qtyInput = form.querySelector('input[name="quantity"]');
        var mainItem = {
          id: parseInt(idInput.value, 10),
          quantity: parseInt((qtyInput && qtyInput.value) || '1', 10) || 1
        };
        // A selected bundle tier stamps its discount on the main line so the Stack
        // discount Function can apply it (see StackBundle → setBundleDeal).
        if (self._bundleDeal) mainItem.properties = { __stack_bundle: self._bundleDeal };
        var items = [mainItem];

        // Fold in anything selected in a sibling "with the main Add to cart" upsell.
        var upsells = self.querySelectorAll('stack-upsell[data-mode="with_main"]');
        for (var i = 0; i < upsells.length; i++) {
          if (upsells[i]._items) items = items.concat(upsells[i]._items());
        }
        stackLog('items to add', items);

        var buttons = form.querySelectorAll('[data-atc-button], [data-sticky-button]');
        var j;
        for (j = 0; j < buttons.length; j++) buttons[j].disabled = true;

        var failed = false;
        stackCartAdd(items)
          .then(function (added) {
            stackLog('added — /cart/add.js OK', added && added.items && added.items.length + ' line(s)');
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            try { document.dispatchEvent(new CustomEvent('cart:build')); } catch (err) {}
            // Refresh the HOST theme's cart UI (count bubble, drawer contents)
            // and open its drawer. Horizon of either generation is handled by
            // the built-in; everything else goes through the theme bridge.
            notifyCartUpdate('add', true);
          })
          ['catch'](function (err) {
            stackLogErr('add-to-cart flow failed —', err);
            failed = true;
          })
          .then(function () {
            for (j = 0; j < buttons.length; j++) buttons[j].disabled = false;
            self._emit();
            // Fetch genuinely failed (offline, etc.) — fall back to a real
            // navigation so the shopper isn't stuck on a dead button, mirroring
            // Shopify's own no-JS behaviour.
            if (failed) { stackLog('falling back to a real form submit after failure'); form.submit(); }
          });
    };

    /* --- internal lookups --- */
    StackProduct.prototype._byId = function (id) {
      id = String(id);
      return this.variants.filter(function (v) { return String(v.id) === id; })[0] || null;
    };
    StackProduct.prototype._firstAvailable = function () {
      return this.variants.filter(function (v) { return v.available; })[0] || null;
    };
    StackProduct.prototype._matchOptions = function (opts) {
      return this.variants.filter(function (v) {
        if (!v.options || v.options.length !== opts.length) return false;
        for (var i = 0; i < opts.length; i++) {
          if (opts[i] != null && v.options[i] !== opts[i]) return false;
        }
        return true;
      })[0] || null;
    };

    /* --- public state --- */
    Object.defineProperty(StackProduct.prototype, 'state', {
      get: function () {
        return {
          variant: this.currentVariant,
          quantity: this.quantity,
          optionValues: this.selectedOptions.slice(),
          moneyFormat: this.moneyFormat,
          bundleDeal: this._bundleDeal || ''
        };
      }
    });

    /* --- public API (called by input blocks) --- */
    StackProduct.prototype.selectOption = function (optionIndex, value) {
      this.explicitVariant = true;
      var idx = (parseInt(optionIndex, 10) || 1) - 1;
      var opts = this.selectedOptions.slice();
      opts[idx] = value;
      var variant = this._matchOptions(opts);
      if (!variant) {
        // No exact combo → fall back to a variant carrying the just-picked
        // value, preferring an AVAILABLE one so picking a value never lands on
        // a sold-out variant while a buyable one with that value exists.
        var carrying = this.variants.filter(function (v) { return v.options[idx] === value; });
        variant = carrying.filter(function (v) { return v.available; })[0] || carrying[0] || null;
      }
      this._setVariant(variant, opts);
    };

    StackProduct.prototype.selectVariantId = function (id) {
      this.explicitVariant = true;
      this._setVariant(this._byId(id), null);
    };

    StackProduct.prototype.setQuantity = function (n) {
      n = parseInt(n, 10);
      if (isNaN(n) || n < 1) n = 1;
      // The bundle deal is DERIVED from the quantity, before anyone reads the
      // new state: stepping the quantity off a tier drops the discount instead
      // of leaving a stale attribute on whatever ends up in the cart.
      if (this._bundleResolver) this._bundleDeal = this._bundleResolver(n);
      if (n === this.quantity) { this._emit(); return; }
      this.quantity = n;
      this._emit();
    };

    /* The active bundle deal, e.g. "p:40@5:Bundle discount" or "" for none.
       Normally derived via _bundleResolver (registered by StackBundle) on every
       setQuantity; this setter remains for custom blocks without a resolver.
       handleCartSubmit stamps the value onto the main line as the
       __stack_bundle attribute so the discount Function applies it. */
    StackProduct.prototype.setBundleDeal = function (deal) {
      this._bundleDeal = deal || '';
    };

    StackProduct.prototype._setVariant = function (variant, fallbackOpts) {
      this.currentVariant = variant;
      this.selectedOptions = variant ? variant.options.slice() : (fallbackOpts || this.selectedOptions);
      this._updateUrl(variant);
      this._emit();
    };

    StackProduct.prototype._updateUrl = function (variant) {
      if (!variant || !window.history || !window.history.replaceState) return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* no-op */ }
    };

    StackProduct.prototype._emit = function () {
      this.dispatchEvent(new CustomEvent('stack:variant-change', {
        bubbles: true,
        detail: this.state
      }));
    };

    return StackProduct;
  })();

  /* =====================================================================
     Shared mixin for blocks that bind to the controller
     ===================================================================== */
  function bindController(proto) {
    proto.connectedCallback = function () {
      this.controller = findController(this);
      this._boundChange = this._boundChange || this._onChange.bind(this);
      if (this.controller) {
        this.controller.addEventListener('stack:variant-change', this._boundChange);
      }
      // Wire interactive handlers once per element instance; reconnects (DOM
      // moves in the theme editor / morph) re-run connectedCallback but must not
      // re-bind click listeners or re-apply the bundle default quantity.
      if (this._wire && !this._wired) { this._wired = true; this._wire(); }
      if (this.controller && this.controller.ready) this._sync(this.controller.state);
    };
    proto.disconnectedCallback = function () {
      if (this.controller && this._boundChange) {
        this.controller.removeEventListener('stack:variant-change', this._boundChange);
      }
    };
    proto._onChange = function (e) { this._sync(e.detail); };
    proto._sync = proto._sync || function () {};
  }

  function makeElement(setup) {
    function El() { return Reflect.construct(HTMLElement, [], El); }
    El.prototype = Object.create(HTMLElement.prototype);
    El.prototype.constructor = El;
    setup(El.prototype);
    bindController(El.prototype);
    return El;
  }

  /* =====================================================================
     <stack-price>
     ===================================================================== */
  var StackPrice = makeElement(function (p) {
    p._sync = function (state) {
      var v = state.variant;
      var priceEl = this.querySelector('[data-price]');
      var compareEl = this.querySelector('[data-compare]');
      var saveEl = this.querySelector('[data-save]');
      var saveTextEl = this.querySelector('[data-save-text]') || saveEl;
      var subEl = this.querySelector('[data-subline]');
      var soldEl = this.querySelector('[data-soldout]');
      var available = !!(v && v.available);
      if (soldEl) soldEl.hidden = available;
      if (!v) { if (saveEl) saveEl.hidden = true; return; }
      // Optional: show the total for the selected quantity (price × qty). In
      // that mode the active bundle deal is applied too — the price row must
      // promise the same total the tier tile and the checkout do. The unit-price
      // mode (qty-price off) never applies the deal: it's a line-level discount.
      var qtyMode = this.getAttribute('data-qty-price') === 'true';
      var qmul = qtyMode ? (state.quantity || 1) : 1;
      var deal = qtyMode ? (state.bundleDeal || '') : '';
      var net = deal ? bundleNet(v.price, qmul, deal) : v.price * qmul;
      if (priceEl) priceEl.textContent = formatMoney(net, state.moneyFormat);
      var onSale = v.compareAtPrice != null && v.compareAtPrice > v.price;
      var dealCompare = !onSale && net < v.price * qmul;
      if (compareEl) {
        compareEl.hidden = !onSale && !dealCompare;
        if (onSale) compareEl.textContent = formatMoney(v.compareAtPrice * qmul, state.moneyFormat);
        else if (dealCompare) compareEl.textContent = formatMoney(v.price * qmul, state.moneyFormat);
      }
      if (saveEl) {
        saveEl.hidden = !onSale || !available;
        if (onSale && available) {
          var pct = Math.round(((v.compareAtPrice - v.price) / v.compareAtPrice) * 100);
          var amount = formatMoney((v.compareAtPrice - v.price) * qmul, state.moneyFormat);
          var tpl = saveEl.getAttribute('data-template') || 'Save {value}%';
          saveTextEl.textContent = tpl
            .replace(/\{percent\}/g, pct)
            .replace(/\{value\}/g, pct)
            .replace(/\{amount\}/g, amount);
        }
      }
      if (subEl) {
        var parts = [];
        if (v.unitPrice != null && v.unitUnit) {
          var ref = (v.unitRef != null && v.unitRef !== 1) ? (v.unitRef + ' ') : '';
          parts.push(formatMoney(v.unitPrice, state.moneyFormat) + '/' + ref + v.unitUnit);
        }
        var tax = subEl.getAttribute('data-tax');
        if (tax) parts.push(tax);
        subEl.textContent = parts.join(', ');
        subEl.hidden = parts.length === 0;
      }
    };
  });

  /* =====================================================================
     <stack-title> — variant suffix
     ===================================================================== */
  var StackTitle = makeElement(function (p) {
    p._sync = function (state) {
      var suffix = this.querySelector('[data-title-suffix]');
      if (!suffix) return;
      suffix.textContent = state.variant ? state.variant.title : '';
    };
  });

  /* =====================================================================
     <stack-variant-picker> — image/colour swatches
     ===================================================================== */
  var StackVariantPicker = makeElement(function (p) {
    p._wire = function () {
      var self = this;
      this.optionIndex = parseInt(this.getAttribute('data-option-index'), 10) || 1;
      this.querySelectorAll('[data-swatch]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (self.controller) self.controller.selectOption(self.optionIndex, btn.getAttribute('data-value'));
        });
      });
    };
    p._sync = function (state) {
      var idx = this.optionIndex - 1;
      // undefined when nothing is selected (product fully sold out) → no swatch
      // is pressed and the label shows no value.
      var current = state.optionValues[idx];
      var opts = state.optionValues;
      var variants = this.controller ? this.controller.variants : null;
      this.querySelectorAll('[data-swatch]').forEach(function (btn) {
        var val = btn.getAttribute('data-value');
        btn.setAttribute('aria-pressed', String(val === current));
        if (!variants) return;
        // Available if some available variant carries this value while matching
        // every OTHER currently-selected option.
        var avail = variants.some(function (v) {
          if (!v.available || v.options[idx] !== val) return false;
          for (var i = 0; i < opts.length; i++) {
            if (i === idx) continue;
            if (opts[i] != null && v.options[i] !== opts[i]) return false;
          }
          return true;
        });
        // Never disable the currently selected value (so you can always move off it).
        btn.disabled = !avail && val !== current;
        btn.classList.toggle('is-soldout', !avail);
      });
      var label = this.querySelector('[data-selected-value]');
      if (label) label.textContent = current == null ? '' : current;
    };
  });

  /* =====================================================================
     <stack-bundle> — quantity tiers
     ===================================================================== */
  var StackBundle = makeElement(function (p) {
    // Combine a tier's encoded deal ("p:15" / "a:10" / "") with the deal name off
    // the parent <stack-bundle> → the full __stack_bundle attribute value.
    function tierDeal(tier, name) {
      var code = tier.getAttribute('data-deal') || '';
      if (!code) return '';
      return name ? code + ':' + name : code;
    }
    p._wire = function () {
      var self = this;
      var name = this.getAttribute('data-deal-name') || '';
      this._tiers = Array.prototype.slice.call(this.querySelectorAll('[data-tier]'));
      function tierQty(tier) { return parseInt(tier.getAttribute('data-quantity'), 10) || 1; }
      function dealForQty(qty) {
        for (var i = 0; i < self._tiers.length; i++) {
          if (tierQty(self._tiers[i]) === qty) return tierDeal(self._tiers[i], name);
        }
        return '';
      }
      this._tiers.forEach(function (tier) {
        tier.addEventListener('click', function () {
          if (self.controller) self.controller.setQuantity(tierQty(tier));
        });
      });
      // The controller derives the active deal from this map on EVERY quantity
      // change (see setQuantity) — the deal always matches the tier the current
      // quantity actually earns, and clears when no tier matches.
      if (this.controller) {
        this.controller._bundleResolver = dealForQty;
        this.controller.setBundleDeal(dealForQty(this.controller.quantity || 1));
      }
      // Apply the default tier's quantity once on init (the resolver sets its deal).
      var def = this._tiers.filter(function (t) { return t.getAttribute('data-default') === 'true'; })[0];
      if (def && this.controller) this.controller.setQuantity(tierQty(def));
    };
    p._sync = function (state) {
      var v = state && state.variant;
      var fmt = state && state.moneyFormat;
      (this._tiers || []).forEach(function (tier) {
        var q = parseInt(tier.getAttribute('data-quantity'), 10) || 1;
        tier.setAttribute('aria-pressed', String(q === state.quantity));

        // Live pricing: recompute this tier's totals from the selected variant,
        // honouring the encoded deal (bundleNet mirrors the discount Function).
        if (!v || v.price == null) return;
        var divisor = parseFloat(tier.getAttribute('data-divisor')) || q;
        var gross = v.price * q;
        var net = bundleNet(v.price, q, tier.getAttribute('data-deal'));

        var totalEl = tier.querySelector('[data-bundle-total]');
        var compareEl = tier.querySelector('[data-bundle-compare]');
        var unitEl = tier.querySelector('[data-bundle-unit]');
        if (totalEl) totalEl.textContent = formatMoney(net, fmt);
        if (compareEl) compareEl.textContent = formatMoney(gross, fmt);
        if (unitEl && divisor > 0) unitEl.textContent = formatMoney(Math.round(net / divisor), fmt);
      });
    };
  });

  /* =====================================================================
     <stack-quantity> — standalone stepper
     ===================================================================== */
  var StackQuantity = makeElement(function (p) {
    p._wire = function () {
      var self = this;
      this.querySelectorAll('[data-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (!self.controller) return;
          var dir = btn.getAttribute('data-step') === 'up' ? 1 : -1;
          self.controller.setQuantity(self.controller.state.quantity + dir);
        });
      });
    };
    p._sync = function (state) {
      var out = this.querySelector('[data-qty-value]');
      if (out) out.textContent = state.quantity;
    };
  });

  /* =====================================================================
     <stack-atc> — add-to-cart (hidden id + quantity sync, availability, stepper)
     ===================================================================== */
  var StackAtc = makeElement(function (p) {
    p._wire = function () {
      var self = this;
      this._idInput = this.querySelector('input[name="id"]');
      this._qtyInput = this.querySelector('input[name="quantity"]');
      this._button = this.querySelector('[data-atc-button]');
      this._label = this.querySelector('[data-atc-label]');
      this._stepValue = this.querySelector('[data-step-value]');
      this._prices = this.querySelector('[data-atc-prices]');
      this._priceEl = this.querySelector('[data-atc-price]');
      this._compareEl = this.querySelector('[data-atc-compare]');
      // inline stepper (only present when show_quantity is true)
      this.querySelectorAll('[data-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (!self.controller) return;
          var dir = btn.getAttribute('data-step') === 'up' ? 1 : -1;
          self.controller.setQuantity(self.controller.state.quantity + dir);
        });
      });
    };
    p._sync = function (state) {
      var v = state.variant;
      var available = !!(v && v.available);
      if (this._idInput && v) this._idInput.value = v.id;
      if (this._button) this._button.disabled = !available;
      if (this._label) {
        this._label.textContent = available
          ? (this._label.getAttribute('data-available') || this._label.textContent)
          : (this._label.getAttribute('data-soldout') || 'Sold out');
      }
      if (this._qtyInput) this._qtyInput.value = state.quantity;
      if (this._stepValue) this._stepValue.textContent = state.quantity;
      if (this._prices) this._prices.hidden = !available;
      var qmul = (this.getAttribute('data-qty-price') === 'true') ? (state.quantity || 1) : 1;
      // Same contract as stack-price: in qty-price mode the active bundle deal
      // is applied, so the button price equals the tier tile and the checkout.
      var deal = (this.getAttribute('data-qty-price') === 'true') ? (state.bundleDeal || '') : '';
      var net = (v && deal) ? bundleNet(v.price, qmul, deal) : (v ? v.price * qmul : 0);
      if (this._priceEl && v) this._priceEl.textContent = formatMoney(net, state.moneyFormat);
      if (this._compareEl) {
        var onSale = v && v.compareAtPrice != null && v.compareAtPrice > v.price;
        var dealCompare = v && !onSale && net < v.price * qmul;
        this._compareEl.hidden = !onSale && !dealCompare;
        if (onSale) this._compareEl.textContent = formatMoney(v.compareAtPrice * qmul, state.moneyFormat);
        else if (dealCompare) this._compareEl.textContent = formatMoney(v.price * qmul, state.moneyFormat);
      }
    };
  });

  /* =====================================================================
     <stack-sticky-atc> — floating variant-aware add bar
     ===================================================================== */
  var StackStickyAtc = makeElement(function (p) {
    p._wire = function () {
      this._idInput = this.querySelector('[data-sticky-id]');
      this._qtyInput = this.querySelector('[data-sticky-qty]');
      this._button = this.querySelector('[data-sticky-button]');
      this._imageEl = this.querySelector('[data-sticky-image]');
      this._priceEl = this.querySelector('[data-sticky-price]');
      this._variantEl = this.querySelector('[data-sticky-variant]');
      this._sepEl = this.querySelector('[data-sticky-sep]');

      // Reveal once the main add-to-cart (or, failing that, the gallery) has been
      // scrolled up past the top of the viewport.
      var self = this;

      // Parked on <body>, the bar's form no longer bubbles its submit through
      // <stack-product>, so the controller's interception never fires — the form
      // would do a native full-page POST that skips the bundle deal, the upsell
      // fold-in and the AJAX cart flow. Route it to the controller by hand.
      this.addEventListener('submit', function (e) {
        if (self.controller && self.controller.handleCartSubmit) self.controller.handleCartSubmit(e);
      });
      var trigger = this.controller &&
        (this.controller.querySelector('[data-atc-button]') ||
         this.controller.querySelector('.stack-product__media'));
      if (trigger && 'IntersectionObserver' in window) {
        this._obs = new IntersectionObserver(function (entries) {
          var e = entries[0];
          var scrolledPast = !e.isIntersecting && e.boundingClientRect.top < 0;
          self.classList.toggle('is-visible', scrolledPast);
        }, { threshold: 0 });
        this._obs.observe(trigger);
      } else {
        this.classList.add('is-visible');
      }
    };
    p._sync = function (state) {
      var v = state.variant;
      var available = !!(v && v.available);
      if (this._idInput && v) this._idInput.value = v.id;
      if (this._qtyInput) this._qtyInput.value = state.quantity;
      if (this._button) this._button.disabled = !available;
      if (!v) return;
      if (this._priceEl) this._priceEl.textContent = formatMoney(v.price, state.moneyFormat);
      if (this._imageEl && v.image) this._imageEl.src = v.image;
      if (this._variantEl) {
        var vt = (v.title && v.title !== 'Default Title') ? v.title : '';
        this._variantEl.textContent = vt;
        this._variantEl.hidden = !vt;
        if (this._sepEl) this._sepEl.hidden = !vt;
      }
    };
  });

  /* The bar is `position: fixed`, but it is rendered inside the details column,
     which turns `position: sticky` on desktop (stack.css) — and a sticky element
     always creates a stacking context. That traps the bar inside the section:
     every later section paints over it no matter how large its z-index is, which
     is why the bar hid behind content on desktop but behaved on mobile, where
     the sticky column rule doesn't apply.

     So park it on <body>, out of reach of any ancestor's stacking context — the
     same trick the size-guide and shop-the-look drawers use. The bar's CSS is
     keyed to its instance id, so it travels with the element. The controller has
     to be captured BEFORE the move, since `closest()` can't reach it afterwards
     (findController falls back to `_host`). */
  (function (p) {
    var connected = p.connectedCallback;
    p.connectedCallback = function () {
      if (this.parentNode !== document.body) {
        this._host = findController(this) || this._host;
        // A theme-editor re-render mounts a fresh bar while the previous one
        // sits on <body> — outside the replaced section, so nothing else would
        // ever clean it up. Scan the parked bars directly: getElementById would
        // hand back the one still in the section (it comes first in the document).
        var parked = document.querySelectorAll('body > stack-sticky-atc');
        for (var i = 0; i < parked.length; i++) {
          if (parked[i] !== this && parked[i].id === this.id) {
            parked[i].parentNode.removeChild(parked[i]);
          }
        }
        // Re-enters this callback with the element parked, which then wires it.
        document.body.appendChild(this);
        return;
      }
      connected.call(this);
    };
  })(StackStickyAtc.prototype);

  // Editor housekeeping: a parked bar outlives the section it came from, so drop
  // the ones whose product section is gone (block removed, section deleted).
  document.addEventListener('shopify:section:unload', function () {
    var bars = document.querySelectorAll('body > stack-sticky-atc');
    for (var i = 0; i < bars.length; i++) {
      var host = bars[i]._host;
      if (!host || !document.contains(host)) bars[i].parentNode.removeChild(bars[i]);
    }
  });

  /* =====================================================================
     <stack-stock-bar> — animated "stock left" urgency bar
     ===================================================================== */
  var StackStockBar = makeElement(function (p) {
    p._sync = function (state) {
      var v = state.variant;
      var pct;
      if (this.getAttribute('data-mode') === 'inventory') {
        // Marked for the Section Rendering API; until then use controller data.
        if (!v || v.inventory == null) return;
        var max = parseFloat(this.getAttribute('data-max')) || 0;
        pct = max > 0 ? Math.round((v.inventory / max) * 100) : 0;
      } else {
        pct = parseFloat(this.getAttribute('data-percent')) || 0;
      }
      if (pct < 0) pct = 0;
      if (pct > 100) pct = 100;

      var fill = this.querySelector('[data-stock-fill]');
      if (fill) fill.style.width = pct + '%';

      var bar = this.querySelector('[role="progressbar"]');
      if (bar) bar.setAttribute('aria-valuenow', pct);

      var textEl = this.querySelector('[data-stock-text]');
      if (textEl) {
        var tpl = textEl.getAttribute('data-template') || '';
        var count = (v && v.inventory != null && v.inventory > 0) ? v.inventory : '';
        textEl.textContent = tpl.replace(/\{percent\}/g, pct).replace(/\{count\}/g, count);
      }
    };
  });

  /* =====================================================================
     <stack-gallery> — Swiper main + thumbs, variant sync, zoom lightbox
     ===================================================================== */
  var StackGallery = (function () {
    function El() { return Reflect.construct(HTMLElement, [], El); }
    El.prototype = Object.create(HTMLElement.prototype);
    El.prototype.constructor = El;

    El.prototype.connectedCallback = function () {
      this.controller = findController(this);
      this._boundChange = this._boundChange || this._onChange.bind(this);
      if (this.controller) this.controller.addEventListener('stack:variant-change', this._boundChange);
      this._wireInteractions();
      this._wireZoom();
      var self = this;
      // Grid layout: the desktop media wall is pure CSS — Swiper only runs
      // below 990px (the mobile slider). Crossing the breakpoint tears down /
      // re-creates the slider accordingly.
      this._isGrid = this.getAttribute('data-layout') === 'grid';
      if (this._isGrid && !this._mq) {
        this._mq = window.matchMedia('(min-width: 990px)');
        this._onMq = function () { self._applyLayout(); };
        if (this._mq.addEventListener) this._mq.addEventListener('change', this._onMq);
        else this._mq.addListener(this._onMq);
      }

      // Optional per-variant media: { "<variantId>": [ {s,t,a}, … ] }. When
      // present, variant change swaps the whole media set (Mode 1). Otherwise
      // data-reorder moves the variant's image to the first grid tile (Mode 2).
      var vm = this.querySelector('script[data-variant-media]');
      if (vm) { try { this._vmap = JSON.parse(vm.textContent); } catch (e) { this._vmap = null; } }
      // The variant currently on screen (server-rendered), so re-emits for the
      // same variant — incl. the one _initSwiper fires — don't rebuild in a loop.
      this._currentVmId = this.getAttribute('data-vm-current') || null;
      this._reorder = this.getAttribute('data-reorder') === 'true';
      // Original slide order, so grid reordering isn't cumulative.
      this._origOrder = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-main] .swiper-slide'));

      this._applyLayout();
    };

    El.prototype._gridActive = function () {
      return !!(this._isGrid && this._mq && this._mq.matches);
    };

    El.prototype._applyLayout = function () {
      var self = this;
      if (this._gridActive()) { this._destroy(); return; }
      loadSwiper()
        .then(function (Swiper) { self._initSwiper(Swiper); })
        .catch(function () { self.classList.add('stack-gallery--no-swiper'); });
    };

    El.prototype.disconnectedCallback = function () {
      if (this.controller && this._boundChange) {
        this.controller.removeEventListener('stack:variant-change', this._boundChange);
      }
      if (this._mq && this._onMq) {
        if (this._mq.removeEventListener) this._mq.removeEventListener('change', this._onMq);
        else this._mq.removeListener(this._onMq);
      }
      this._destroy();
    };

    // Works whether or not Swiper is available. Thumbnail clicks are delegated
    // off the gallery element (so they survive block re-renders), and a scroll
    // listener keeps the active thumbnail in sync when the native-scroll fallback
    // (no Swiper) is in use.
    El.prototype._wireInteractions = function () {
      if (this._wired) return;
      this._wired = true;
      var self = this;
      this.addEventListener('click', function (e) {
        var thumb = e.target.closest && e.target.closest('[data-thumb]');
        if (thumb) {
          var thumbs = Array.prototype.slice.call(self.querySelectorAll('[data-thumb]'));
          var idx = thumbs.indexOf(thumb);
          if (idx >= 0) self._goTo(idx);
          return;
        }
        // Desktop grid: the zoom button is hidden — clicking any tile opens the
        // lightbox on that image instead.
        if (self._gridActive() && self._lbOpenForSlide) {
          var slide = e.target.closest && e.target.closest('[data-gallery-main] .swiper-slide');
          if (slide) self._lbOpenForSlide(slide);
        }
      });
      var wrapper = this.querySelector('[data-gallery-main] .swiper-wrapper');
      if (wrapper) {
        wrapper.addEventListener('scroll', function () {
          if (self.main || self._scrollRaf) return; // Swiper active → it owns sync
          self._scrollRaf = requestAnimationFrame(function () {
            self._scrollRaf = null;
            self._syncFromScroll(wrapper);
          });
        }, { passive: true });
      }
    };

    // Fallback active-slide detection from scroll position (no Swiper).
    El.prototype._syncFromScroll = function (wrapper) {
      if (this.main) return;
      var slides = wrapper.querySelectorAll('.swiper-slide');
      if (!slides.length) return;
      var center = wrapper.scrollLeft + wrapper.clientWidth / 2;
      var best = 0, bestDist = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var c = slides[i].offsetLeft + slides[i].offsetWidth / 2;
        var d = Math.abs(c - center);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      this._activeIndex = best;
      this._reflectThumbs();
    };

    El.prototype._initSwiper = function (Swiper) {
      // Grid on desktop is pure CSS — never let a late Swiper load (e.g. a
      // resize while the CDN request was in flight) take the wall over.
      if (this._gridActive()) return;
      // A theme re-render (editor, morph) can run connectedCallback again while
      // the element is still around; tear down any previous instance first so we
      // never stack two Swipers on the same DOM (which leaves slideTo moving the
      // logical index without translating the wrapper).
      if (this.main) this._destroy();
      var mainEl = this.querySelector('[data-gallery-main]');
      if (!mainEl) return;
      var spv = parseFloat(this.getAttribute('data-spv')) || 1;
      var space = parseFloat(this.getAttribute('data-space')) || 0;
      var navOn = this.getAttribute('data-nav') === '1';
      var prev = this.querySelector('[data-gallery-prev]');
      var next = this.querySelector('[data-gallery-next]');
      // When teasing, inset the slides on mobile so the next card peeks into the
      // side padding rather than being clipped at the container edge.
      var tease = this.classList.contains('stack-gallery--tease');
      var mobileOffset = tease ? 16 : 0;
      var self = this;
      // Start on the selected variant's image (URL ?variant=… on load), so the
      // gallery never flashes the default image first.
      var initialSlide = this._slideIndexForState();
      try {
        var swiperOptions = {
          slidesPerView: spv,
          spaceBetween: space,
          initialSlide: initialSlide,
          slidesOffsetBefore: mobileOffset,
          slidesOffsetAfter: mobileOffset,
          observer: true,
          observeParents: true,
          observeSlideChildren: true,
          breakpoints: { 990: { slidesOffsetBefore: 0, slidesOffsetAfter: 0 } }
        };

        if (this.querySelector('.swiper-zoom-container')) {
          swiperOptions.zoom = true;
        } else {
          swiperOptions.zoom = false;
        }

        if (navOn && prev && next) {
          swiperOptions.navigation = { prevEl: prev, nextEl: next };
        } else {
          swiperOptions.navigation = false;
        }

        this.main = new Swiper(mainEl, swiperOptions);
        this._activeIndex = initialSlide;
        this.main.on('slideChange', function () {
          self._activeIndex = self.main.activeIndex;
          self._reflectThumbs();
        });
        this._reflectThumbs();

        // Swiper measures slide widths at init. If it ran before the gallery had
        // laid out (fonts/lazy images), those sizes can be stale/zero and slideTo
        // then updates the active index without visually moving the wrapper. Force
        // a geometry refresh once images and layout settle.
        this._refreshGeometry();
      } catch (err) {
        console.error('Swiper init failed:', err);
        this.classList.add('stack-gallery--no-swiper');
      }

      // Sync to the currently selected variant's media.
      if (this.controller && this.controller.ready) this._onChange({ detail: this.controller.state });
    };

    // Re-measure Swiper after the gallery images have loaded / the next frames
    // have painted, so slide positions are correct before any slideTo runs.
    El.prototype._refreshGeometry = function () {
      var self = this;
      var update = function () { if (self.main && self.main.update) self.main.update(); };
      requestAnimationFrame(function () { requestAnimationFrame(update); });
      var imgs = this.querySelectorAll('[data-gallery-main] img');
      for (var i = 0; i < imgs.length; i++) {
        if (imgs[i].complete) continue;
        imgs[i].addEventListener('load', update, { once: true });
      }
      if (document.readyState !== 'complete') {
        window.addEventListener('load', update, { once: true });
      }
    };

    // Index of the slide matching the currently selected variant's media (0 if
    // none / no per-variant image).
    El.prototype._slideIndexForState = function () {
      // Only honour the variant's media when the shopper explicitly asked for
      // that variant (URL ?variant=… or a picker click). On a plain page load we
      // stay on the first media instead of snapping to a default variant.
      if (!this.controller || !this.controller.explicitVariant) return 0;
      var state = this.controller.state;
      var v = state && state.variant;
      if (!v || v.mediaId == null) return 0;
      var slides = this.querySelectorAll('[data-gallery-main] .swiper-slide');
      for (var i = 0; i < slides.length; i++) {
        if (String(slides[i].getAttribute('data-media-id')) === String(v.mediaId)) return i;
      }
      return 0;
    };

    // Move the main gallery to a slide index — Swiper if present, else the
    // native scroll fallback — and keep the active thumbnail in sync.
    El.prototype._goTo = function (index) {
      if (index < 0) return;
      this._activeIndex = index;
      // Desktop grid shows every tile at once — just record the index (it seeds
      // the lightbox) without scrolling anything.
      if (this._gridActive()) return;
      // Reflect the requested index up front so the active thumb always follows
      // the click, even if a Swiper call below throws or no-ops.
      this._reflectThumbs(index);
      if (this.main && typeof this.main.slideTo === 'function') {
        try {
          // Refresh geometry first so slideTo actually translates the wrapper
          // (stale/zero slide sizes otherwise move only the logical index).
          if (this.main.update) this.main.update();
          this.main.slideTo(index);
        } catch (err) { /* fall through to nothing — border already updated */ }
        return;
      }
      // Fallback (no Swiper): scroll the strip to the exact slide position.
      // Uses bounding rects (not scrollIntoView, which fights scroll-snap and
      // lands on the wrong slide) so it is reliable and repeatable.
      var wrapper = this.querySelector('[data-gallery-main] .swiper-wrapper');
      if (!wrapper) return;
      var slides = wrapper.querySelectorAll('.swiper-slide');
      var target = slides[index];
      if (!target) return;
      var delta = target.getBoundingClientRect().left - wrapper.getBoundingClientRect().left;
      wrapper.scrollLeft = wrapper.scrollLeft + delta; // Fallback to instant scroll if smooth scroll fails
    };

    El.prototype._reflectThumbs = function (explicitIndex) {
      var idx = (explicitIndex != null)
        ? explicitIndex
        : (this.main && this.main.activeIndex != null) ? this.main.activeIndex : (this._activeIndex || 0);
      var thumbs = this.querySelectorAll('[data-thumb]');
      for (var i = 0; i < thumbs.length; i++) {
        thumbs[i].classList.toggle('is-active', i === idx);
      }
    };

    El.prototype._destroy = function () {
      if (this.main) { try { this.main.destroy(true, true); } catch (e) {} this.main = null; }
    };

    El.prototype._onChange = function (e) {
      // Ignore the controller's initial "sync" emit on load — only move the
      // gallery once the shopper has explicitly chosen a variant.
      if (!this.controller || !this.controller.explicitVariant) return;
      var v = e.detail && e.detail.variant;
      if (!v) return;

      // Mode 1 — swap the whole media set to this variant's metafield images.
      if (this._vmap) { this._applyVariantMedia(v); return; }

      if (v.mediaId == null) return;

      // Mode 2 — desktop grid: move the variant's image to the first tile.
      if (this._reorder && this._gridActive()) { this._reorderGrid(v); return; }

      // Default (slider) — scroll to the slide holding the variant's media.
      var slides = this.querySelectorAll('[data-gallery-main] .swiper-slide');
      for (var i = 0; i < slides.length; i++) {
        if (String(slides[i].getAttribute('data-media-id')) === String(v.mediaId)) {
          this._goTo(i);
          break;
        }
      }
    };

    // Mode 2: reorder the grid so the selected variant's image is first. Moves
    // the actual DOM node (not CSS `order`) so the grid's nth-child pattern
    // rules re-apply and the promoted tile takes the first/hero position.
    El.prototype._reorderGrid = function (v) {
      var wrapper = this.querySelector('[data-gallery-main] .swiper-wrapper');
      if (!wrapper || !this._origOrder || !this._origOrder.length) return;
      // Restore the original order first so successive changes aren't cumulative.
      for (var i = 0; i < this._origOrder.length; i++) wrapper.appendChild(this._origOrder[i]);
      var match = null;
      for (var j = 0; j < this._origOrder.length; j++) {
        if (String(this._origOrder[j].getAttribute('data-media-id')) === String(v.mediaId)) { match = this._origOrder[j]; break; }
      }
      if (match) wrapper.insertBefore(match, wrapper.firstChild);
    };

    // Mode 1: rebuild the slides (and thumbs) from a variant's media list, then
    // re-lay-out — pure CSS handles the desktop grid; the slider re-inits Swiper.
    El.prototype._applyVariantMedia = function (v) {
      if (String(v.id) === this._currentVmId) return; // already showing this variant
      var list = this._vmap[String(v.id)];
      if (!list || !list.length) return; // no list for this variant → keep current
      this._currentVmId = String(v.id);
      this._setSlides(list);
    };

    El.prototype._setSlides = function (list) {
      var wrapper = this.querySelector('[data-gallery-main] .swiper-wrapper');
      if (!wrapper) return;
      var frag = document.createDocumentFragment();
      list.forEach(function (it, i) {
        var slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.setAttribute('data-media-id', 'vm-' + i);
        var zc = document.createElement('div');
        zc.className = 'swiper-zoom-container';
        var img = document.createElement('img');
        img.src = it.s;
        img.alt = it.a || '';
        img.loading = i === 0 ? 'eager' : 'lazy';
        zc.appendChild(img);
        slide.appendChild(zc);
        frag.appendChild(slide);
      });
      wrapper.textContent = '';
      wrapper.appendChild(frag);
      this._origOrder = Array.prototype.slice.call(wrapper.querySelectorAll('.swiper-slide'));
      this._rebuildThumbs(list);

      if (this._gridActive()) {
        // Desktop grid is pure CSS — the new tiles lay out on their own.
        if (this.main) this._destroy();
        return;
      }
      // Slider: rebuild Swiper over the new slides and start on the first.
      var self = this;
      if (this.main) this._destroy();
      loadSwiper()
        .then(function (Swiper) { self._initSwiper(Swiper); })
        .catch(function () { self.classList.add('stack-gallery--no-swiper'); });
    };

    El.prototype._rebuildThumbs = function (list) {
      var thumbs = this.querySelector('.stack-gallery__thumbs');
      if (!thumbs) return;
      thumbs.hidden = list.length <= 1;
      var frag = document.createDocumentFragment();
      list.forEach(function (it, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'stack-gallery__thumb' + (i === 0 ? ' is-active' : '');
        b.setAttribute('data-thumb', '');
        b.setAttribute('data-media-id', 'vm-' + i);
        b.setAttribute('aria-label', 'View image ' + (i + 1));
        var img = document.createElement('img');
        img.src = it.t;
        img.alt = it.a || '';
        img.loading = 'lazy';
        b.appendChild(img);
        frag.appendChild(b);
      });
      thumbs.textContent = '';
      thumbs.appendChild(frag);
    };

    El.prototype._wireZoom = function () {
      if (this._zoomWired) return;
      var btn = this.querySelector('[data-gallery-zoom]');
      var dialog = this.querySelector('[data-gallery-lightbox]');
      if (!btn || !dialog) return;
      this._zoomWired = true;
      var img = dialog.querySelector('img');
      var close = dialog.querySelector('[data-gallery-lightbox-close]');
      var lbPrev = dialog.querySelector('[data-gallery-lightbox-prev]');
      var lbNext = dialog.querySelector('[data-gallery-lightbox-next]');
      var self = this;

      var galleryImages = function () {
        return Array.prototype.slice.call(
          self.querySelectorAll('[data-gallery-main] .swiper-slide:not(.swiper-slide-duplicate) img')
        );
      };
      var show = function (i) {
        var imgs = galleryImages();
        if (!imgs.length) return;
        i = (i + imgs.length) % imgs.length;
        self._lbIndex = i;
        if (img) img.src = imgs[i].currentSrc || imgs[i].src;
        self._goTo(i); // keep the underlying gallery in sync
      };
      var openAt = function (i) {
        if (dialog.open) return;
        show(i);
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      };
      var open = function () {
        var active = (self.main && self.main.activeIndex != null) ? self.main.activeIndex : (self._activeIndex || 0);
        openAt(active);
      };
      // Desktop grid: tiles open the lightbox directly (the zoom button is
      // hidden there). Maps the clicked slide to its image's lightbox index.
      this._lbOpenForSlide = function (slideEl) {
        var slideImg = slideEl.querySelector('img');
        if (!slideImg) return;
        var i = galleryImages().indexOf(slideImg);
        if (i >= 0) openAt(i);
      };
      var hide = function () {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      };
      btn.addEventListener('click', open);
      if (close) close.addEventListener('click', hide);
      if (lbPrev) lbPrev.addEventListener('click', function (e) { e.stopPropagation(); show((self._lbIndex || 0) - 1); });
      if (lbNext) lbNext.addEventListener('click', function (e) { e.stopPropagation(); show((self._lbIndex || 0) + 1); });
      dialog.addEventListener('click', function (e) { if (e.target === dialog) hide(); });
      dialog.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') show((self._lbIndex || 0) + 1);
        else if (e.key === 'ArrowLeft') show((self._lbIndex || 0) - 1);
      });
    };

    return El;
  })();

  /* =====================================================================
     <stack-video-carousel> — horizontal thumbnails → TikTok-style popup
     ---------------------------------------------------------------------
     The strip itself is plain CSS overflow scroll. Clicking a thumbnail opens a
     fullscreen popup: a vertical scroll-snap track (one video per screen), with
     tap-to-play/pause, mute, next, swipe-down-to-close and a close button. The
     video in view autoplays (muted) via IntersectionObserver; others pause.
     ===================================================================== */
  var VC_ICON = {
    close: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6 6 18"/></svg>',
    next: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>',
    muted: '<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M3 9v6h4l5 4V5L7 9H3z"/><path d="M22 9.4 20.6 8 18 10.6 15.4 8 14 9.4 16.6 12 14 14.6 15.4 16 18 13.4 20.6 16 22 14.6 19.4 12z"/></svg>',
    unmuted: '<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M3 9v6h4l5 4V5L7 9H3zm11.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4zM14 3.2v2.1a6.8 6.8 0 0 1 0 13.4v2.1a8.9 8.9 0 0 0 0-17.6z"/></svg>'
  };

  /* Shared draggable-scrollbar wiring (a thumb synced to an element's horizontal
     overflow scroll). Used by the comparison table AND the video carousel so both
     get the same clean bar. Host gets .is-scrollable when there's overflow. */
  function initDragScrollbar(scroll, bar, thumb, host) {
    if (!scroll || !bar || !thumb) return;
    var raf = 0, moveH, upH;
    function metrics() {
      var max = scroll.scrollWidth - scroll.clientWidth;
      var trackW = bar.clientWidth;
      var ratio = scroll.scrollWidth > 0 ? scroll.clientWidth / scroll.scrollWidth : 1;
      var thumbW = Math.max(28, Math.min(trackW, Math.round(trackW * ratio)));
      return { max: max, trackW: trackW, thumbW: thumbW };
    }
    function update() {
      var m = metrics();
      var scrollable = m.max > 2;
      host.classList.toggle('is-scrollable', scrollable);
      if (!scrollable) return;
      var travel = m.trackW - m.thumbW;
      var x = m.max > 0 ? (scroll.scrollLeft / m.max) * travel : 0;
      thumb.style.width = m.thumbW + 'px';
      thumb.style.transform = 'translateX(' + Math.round(x) + 'px)';
    }
    scroll.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = 0; update(); });
    }, { passive: true });
    thumb.addEventListener('pointerdown', function (e) {
      var m = metrics();
      var travel = m.trackW - m.thumbW;
      if (travel <= 0) return;
      var startX = e.clientX, startScroll = scroll.scrollLeft;
      thumb.classList.add('is-dragging');
      try { thumb.setPointerCapture(e.pointerId); } catch (err) {}
      moveH = function (ev) { scroll.scrollLeft = startScroll + ((ev.clientX - startX) / travel) * m.max; };
      upH = function (ev) {
        thumb.removeEventListener('pointermove', moveH);
        thumb.removeEventListener('pointerup', upH);
        thumb.removeEventListener('pointercancel', upH);
        thumb.classList.remove('is-dragging');
        if (ev && ev.pointerId != null) { try { thumb.releasePointerCapture(ev.pointerId); } catch (err) {} }
      };
      thumb.addEventListener('pointermove', moveH);
      thumb.addEventListener('pointerup', upH);
      thumb.addEventListener('pointercancel', upH);
      e.preventDefault();
    });
    bar.addEventListener('pointerdown', function (e) {
      if (e.target === thumb) return;
      var m = metrics();
      var travel = m.trackW - m.thumbW;
      if (travel <= 0) return;
      var rect = bar.getBoundingClientRect();
      var frac = Math.max(0, Math.min(1, (e.clientX - rect.left - m.thumbW / 2) / travel));
      scroll.scrollTo({ left: frac * m.max, behavior: 'smooth' });
    });
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () { update(); });
      ro.observe(scroll);
      if (scroll.firstElementChild) ro.observe(scroll.firstElementChild);
    } else {
      window.addEventListener('resize', function () { update(); });
    }
    update();
  }

  var StackVideoCarousel = (function () {
    function El() { return Reflect.construct(HTMLElement, [], El); }
    El.prototype = Object.create(HTMLElement.prototype);
    El.prototype.constructor = El;

    El.prototype.connectedCallback = function () {
      if (this._wired) return;
      this._wired = true;
      var self = this;
      // Only items with actual media get a popup slide, so scrolling never lands
      // on an empty (black) screen.
      this._items = Array.prototype.slice.call(this.querySelectorAll('[data-vc-item]'))
        .filter(function (it) {
          return it.getAttribute('data-video') || it.getAttribute('data-external') || it.getAttribute('data-poster');
        });
      this._muted = true;
      this._items.forEach(function (item, i) {
        item.addEventListener('click', function () { self.open(i); });
        item.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.open(i); }
        });
      });

      // Clean draggable scrollbar (same UX as the comparison table).
      initDragScrollbar(
        this.querySelector('[data-vc-scroll]'),
        this.querySelector('[data-vc-bar]'),
        this.querySelector('[data-vc-thumb]'),
        this
      );

      // Inline, muted autoplay previews in the strip when enabled.
      if (this.getAttribute('data-autoplay') === 'true') this._initPreviews();
    };

    El.prototype.disconnectedCallback = function () {
      if (this._onKey) document.removeEventListener('keydown', this._onKey);
      if (this._io) this._io.disconnect();
      if (this._previewIo) this._previewIo.disconnect();
      // Popup lives on <body>, so remove it when the carousel is torn down.
      if (this._popup && this._popup.parentNode) this._popup.parentNode.removeChild(this._popup);
    };

    El.prototype.open = function (index) {
      if (!this._popup) this._build();
      this._popup.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      var slide = this._slides[index];
      // Jump to the clicked video without smooth scroll (instant), then let the
      // observer start playback.
      if (slide) this._track.scrollTop = slide.offsetTop;
    };

    El.prototype.close = function () {
      if (!this._popup) return;
      this._pauseAll();
      this._popup.hidden = true;
      document.documentElement.style.overflow = '';
    };

    El.prototype._build = function () {
      var self = this;
      var pop = document.createElement('div');
      pop.className = 'stack-vcpop';
      pop.hidden = true;

      var track = document.createElement('div');
      track.className = 'stack-vcpop__track';
      pop.appendChild(track);

      this._slides = this._items.map(function (item) {
        var slide = document.createElement('div');
        slide.className = 'stack-vcpop__slide';
        var poster = item.getAttribute('data-poster');
        // Fill the letterbox area behind the video with a blurred poster.
        if (poster) slide.style.setProperty('--vc-poster', 'url("' + poster + '")');
        slide.appendChild(self._media(item));
        track.appendChild(slide);
        return slide;
      });

      pop.appendChild(this._btn('close', VC_ICON.close, 'Close', function () { self.close(); }));
      pop.appendChild(this._btn('next', VC_ICON.next, 'Next video', function () { self._scroll(1); }));
      this._muteBtn = this._btn('mute', VC_ICON.muted, 'Unmute', function () { self._toggleMute(); });
      pop.appendChild(this._muteBtn);

      // Autoplay whichever video is mostly in view; pause the rest.
      this._io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var v = en.target.querySelector('video');
          if (!v) return;
          if (en.isIntersecting && en.intersectionRatio >= 0.6) {
            v.muted = self._muted;
            v.play().catch(function () {});
          } else {
            v.pause();
          }
        });
      }, { root: track, threshold: [0, 0.6, 1] });
      this._slides.forEach(function (s) { self._io.observe(s); });

      // Swipe down (when at the top of a video) closes.
      var startY = 0, atTop = false;
      track.addEventListener('touchstart', function (e) {
        startY = e.touches[0].clientY;
        atTop = track.scrollTop <= 2;
      }, { passive: true });
      track.addEventListener('touchmove', function (e) {
        if (atTop && e.touches[0].clientY - startY > 90) self.close();
      }, { passive: true });

      this._onKey = function (e) { if (e.key === 'Escape') self.close(); };
      document.addEventListener('keydown', this._onKey);

      // Append to <body>, not this element — otherwise a stacking context on an
      // ancestor (transform / filter / sticky bar) traps the popup below other
      // content even at max z-index.
      document.body.appendChild(pop);
      this._popup = pop;
      this._track = track;
    };

    El.prototype._media = function (item) {
      var wrap = document.createElement('div');
      wrap.className = 'stack-vcpop__media';
      var url = item.getAttribute('data-video');
      var ext = item.getAttribute('data-external');
      var poster = item.getAttribute('data-poster');
      if (url) {
        var v = document.createElement('video');
        v.src = url;
        v.loop = true;
        v.muted = true;
        v.playsInline = true;
        v.setAttribute('playsinline', '');
        v.preload = 'metadata';
        if (poster) v.poster = poster;
        v.addEventListener('click', function () {
          if (v.paused) { v.play().catch(function () {}); } else { v.pause(); }
        });
        wrap.appendChild(v);
      } else if (ext) {
        var f = document.createElement('iframe');
        var src = ext.replace('watch?v=', 'embed/');
        f.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'autoplay=1';
        f.allow = 'autoplay; encrypted-media';
        f.setAttribute('allowfullscreen', '');
        wrap.appendChild(f);
      } else if (poster) {
        var img = document.createElement('img');
        img.src = poster;
        wrap.appendChild(img);
      }
      return wrap;
    };

    El.prototype._btn = function (name, icon, label, onClick) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'stack-vcpop__' + name;
      b.setAttribute('aria-label', label);
      b.innerHTML = icon;
      b.addEventListener('click', onClick);
      return b;
    };

    El.prototype._pauseAll = function () {
      if (!this._track) return;
      this._track.querySelectorAll('video').forEach(function (v) { v.pause(); });
    };

    El.prototype._toggleMute = function () {
      this._muted = !this._muted;
      var m = this._muted;
      this._track.querySelectorAll('video').forEach(function (v) { v.muted = m; });
      this._muteBtn.innerHTML = m ? VC_ICON.muted : VC_ICON.unmuted;
      this._muteBtn.setAttribute('aria-label', m ? 'Unmute' : 'Mute');
    };

    El.prototype._scroll = function (dir) {
      if (this._track) this._track.scrollBy({ top: dir * this._track.clientHeight, behavior: 'smooth' });
    };

    // Inject a muted, looping preview <video> into each thumbnail and play only the
    // ones currently in view. Clicking still opens the fullscreen popup.
    El.prototype._initPreviews = function () {
      var vids = [];
      this._items.forEach(function (item) {
        var url = item.getAttribute('data-video');
        var frame = item.querySelector('.stack-vi__frame');
        if (!url || !frame) return;
        var v = document.createElement('video');
        v.className = 'stack-vi__preview';
        v.src = url;
        v.muted = true;
        v.loop = true;
        v.playsInline = true;
        v.setAttribute('playsinline', '');
        v.setAttribute('muted', '');
        v.preload = 'metadata';
        var poster = item.getAttribute('data-poster');
        if (poster) v.poster = poster;
        frame.insertBefore(v, frame.firstChild);
        vids.push(v);
      });
      if (!vids.length) return;
      this.classList.add('stack-vc--has-previews');
      if ('IntersectionObserver' in window) {
        this._previewIo = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting) { en.target.play().catch(function () {}); }
            else { en.target.pause(); }
          });
        }, { threshold: 0.4 });
        var io = this._previewIo;
        vids.forEach(function (v) { io.observe(v); });
      } else {
        vids.forEach(function (v) { v.play().catch(function () {}); });
      }
    };

    return El;
  })();

  /* =====================================================================
     <stack-marquee> — seamless infinite creator marquee (standalone; no
     controller dependency). Renders one group of cards; this fills it to at
     least the viewport width by cloning cards, then appends an identical second
     group and animates the track by exactly one group-width so the loop never
     jumps. Speed is constant px/sec regardless of how many cards there are.
     ===================================================================== */
  var StackMarquee = (function () {
    function StackMarquee() { return Reflect.construct(HTMLElement, [], StackMarquee); }
    StackMarquee.prototype = Object.create(HTMLElement.prototype);
    StackMarquee.prototype.constructor = StackMarquee;

    StackMarquee.prototype.connectedCallback = function () {
      if (this._init) return;
      this._init = true;
      this.track = this.querySelector('[data-mq-track]');
      this.group = this.track && this.track.querySelector('[data-mq-group]');
      if (!this.track || !this.group) return;

      // Capture the pristine single-group markup ONCE, before any fill/clone, so
      // every (re)build starts from the original cards.
      this._seed = this.group.innerHTML;
      this._speed = parseFloat(this.getAttribute('data-speed')) || 40; // px per second

      var self = this;
      this._build();

      // Rebuild when the available width changes (needs more/fewer copies).
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(function () { self._schedule(); });
        this._ro.observe(this);
      } else if (window.addEventListener) {
        this._onResize = function () { self._schedule(); };
        window.addEventListener('resize', this._onResize);
      }
      // Card widths shift once the web font swaps in — remeasure then.
      if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
        document.fonts.ready.then(function () { self._schedule(); });
      }
    };

    StackMarquee.prototype.disconnectedCallback = function () {
      if (this._ro) this._ro.disconnect();
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._t) clearTimeout(this._t);
    };

    StackMarquee.prototype._schedule = function () {
      var self = this;
      if (this._t) clearTimeout(this._t);
      this._t = setTimeout(function () { self._build(); }, 150);
    };

    StackMarquee.prototype._width = function (el) {
      return el.getBoundingClientRect().width;
    };

    StackMarquee.prototype._build = function () {
      var track = this.track;
      var group = this.group;
      if (!track || !group) return;

      // Reset to the pristine single group.
      var clones = track.querySelectorAll('[data-mq-clone]');
      for (var i = 0; i < clones.length; i++) clones[i].remove();
      group.innerHTML = this._seed;
      if (!group.children.length) return;

      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var viewport = this._width(this) || this._width(track.parentNode) || 0;
      var gap = parseFloat(getComputedStyle(group).columnGap) || 0;

      // Fill the group with repeated cards until it spans at least the viewport,
      // so there is never an empty gap at the loop boundary. Capped for safety.
      var originals = Array.prototype.slice.call(group.children);
      var guard = 0;
      while (this._width(group) < viewport && guard < 400) {
        for (var j = 0; j < originals.length; j++) {
          var card = originals[j].cloneNode(true);
          card.setAttribute('aria-hidden', 'true');
          group.appendChild(card);
          guard++;
          if (this._width(group) >= viewport) break;
        }
      }

      var groupWidth = this._width(group);
      // Distance from group1's first card to group2's first card = group width
      // plus the one inter-group gap (the track carries the same gap).
      var shift = groupWidth + gap;

      // Append an identical second group for a seamless hand-off.
      var clone = group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-mq-group');
      clone.setAttribute('data-mq-clone', '');
      track.appendChild(clone);

      this.style.setProperty('--stack-mq-shift', shift + 'px');
      var duration = this._speed > 0 ? shift / this._speed : 0;
      this.style.setProperty('--stack-mq-duration', duration.toFixed(2) + 's');

      var stopped = reduce || duration <= 0;
      this.classList.toggle('is-static', stopped);
      this.classList.add('is-ready');
    };

    return StackMarquee;
  })();

  /* =====================================================================
     <stack-review> — reviews section (standalone). Owns: the "Show more"
     reveal, click-to-play media (uploaded video OR external embed), the
     per-card "Read more" clamp toggle, and the "Show original" translation
     toggle. All are progressive enhancements — without JS the section is a
     readable static grid.
     ===================================================================== */
  var StackReview = (function () {
    function StackReview() { return Reflect.construct(HTMLElement, [], StackReview); }
    StackReview.prototype = Object.create(HTMLElement.prototype);
    StackReview.prototype.constructor = StackReview;

    function toEmbed(url) {
      var yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
      if (yt) return 'https://www.youtube.com/embed/' + yt[1] + '?autoplay=1';
      var vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      if (vm) return 'https://player.vimeo.com/video/' + vm[1] + '?autoplay=1';
      return url;
    }

    StackReview.prototype.connectedCallback = function () {
      if (this._init) return;
      this._init = true;
      this._wireShowMore();
      this._wireMedia();
      this._wireText();
      this._wireSlider();
    };

    StackReview.prototype._wireSlider = function () {
      if (!this.classList.contains('stack-review--slider')) return;
      var grid = this.querySelector('[data-review-grid]');
      if (!grid) return;
      var prev = this.querySelector('[data-review-prev]');
      var next = this.querySelector('[data-review-next]');
      function step(dir) {
        var card = grid.querySelector('.stack-rev-card');
        var gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
        var w = card ? card.getBoundingClientRect().width + gap : grid.clientWidth * 0.8;
        grid.scrollBy({ left: dir * w, behavior: 'smooth' });
      }
      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });

      // Finite (not infinite) track: hide the prev arrow at the start and the
      // next arrow at the end; hide both if everything fits with no scroll.
      function update() {
        var max = grid.scrollWidth - grid.clientWidth;
        var x = grid.scrollLeft;
        var noScroll = max <= 2;
        if (prev) prev.classList.toggle('is-off', noScroll || x <= 1);
        if (next) next.classList.toggle('is-off', noScroll || x >= max - 1);
      }
      var self = this;
      grid.addEventListener('scroll', function () {
        if (self._sraf) return;
        self._sraf = requestAnimationFrame(function () { self._sraf = 0; update(); });
      }, { passive: true });
      if ('ResizeObserver' in window) {
        this._sro = new ResizeObserver(update);
        this._sro.observe(grid);
      } else {
        this._sresize = update;
        window.addEventListener('resize', update);
      }
      if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
        document.fonts.ready.then(update);
      }
      update();
    };

    StackReview.prototype.disconnectedCallback = function () {
      if (this._sro) this._sro.disconnect();
      if (this._sresize) window.removeEventListener('resize', this._sresize);
    };

    StackReview.prototype._wireShowMore = function () {
      var grid = this.querySelector('[data-review-grid]');
      var wrap = this.querySelector('[data-review-more]');
      if (!grid || !wrap) return;
      var btn = wrap.querySelector('[data-review-more-btn]');
      var textEl = wrap.querySelector('[data-review-more-text]') || btn;
      var initial = parseInt(this.getAttribute('data-initial'), 10);
      var cards = Array.prototype.slice.call(grid.children);
      if (!btn || isNaN(initial) || initial <= 0 || cards.length <= initial) {
        wrap.hidden = true;
        return;
      }
      var expanded = false;
      var moreLabel = btn.getAttribute('data-more') || 'Show more';
      var lessLabel = btn.getAttribute('data-less') || 'Show less';
      function apply() {
        for (var i = 0; i < cards.length; i++) {
          cards[i].classList.toggle('is-hidden', !expanded && i >= initial);
        }
        if (textEl) textEl.textContent = expanded ? lessLabel : moreLabel;
        btn.classList.toggle('is-expanded', expanded);
      }
      apply();
      wrap.hidden = false;
      btn.addEventListener('click', function () { expanded = !expanded; apply(); });
    };

    StackReview.prototype._wireMedia = function () {
      var plays = this.querySelectorAll('[data-review-play]');
      Array.prototype.forEach.call(plays, function (btn) {
        btn.addEventListener('click', function () {
          var media = btn.closest('[data-review-media]');
          if (!media) return;
          var embed = media.getAttribute('data-review-embed');
          if (embed) {
            var frame = document.createElement('iframe');
            frame.src = toEmbed(embed);
            frame.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
            frame.setAttribute('allowfullscreen', '');
            frame.className = 'stack-rev-card__video';
            var old = media.querySelector('.stack-rev-card__img, .stack-rev-card__video');
            if (old) old.remove();
            media.insertBefore(frame, btn);
            media.classList.add('is-playing');
            btn.remove();
          } else {
            var v = media.querySelector('video');
            if (v) {
              v.setAttribute('controls', '');
              media.classList.add('is-playing');
              var p = v.play();
              if (p && p.catch) p.catch(function () {});
            }
          }
        });
      });
    };

    StackReview.prototype._wireText = function () {
      // "Read more" — only surfaced when the body is actually clamped/overflowing.
      var wraps = this.querySelectorAll('[data-review-text]');
      Array.prototype.forEach.call(wraps, function (wrap) {
        var body = wrap.querySelector('[data-review-body]');
        var btn = wrap.querySelector('[data-review-readmore]');
        if (!body || !btn) return;
        if (body.scrollHeight - body.clientHeight > 2) {
          btn.hidden = false;
          var more = btn.getAttribute('data-more') || 'Read more';
          var less = btn.getAttribute('data-less') || 'Read less';
          btn.addEventListener('click', function () {
            var open = body.classList.toggle('is-expanded');
            btn.textContent = open ? less : more;
          });
        }
      });
      // "Show original" — swap between translated body and the original text.
      var origs = this.querySelectorAll('[data-review-original]');
      Array.prototype.forEach.call(origs, function (link) {
        var card = link.closest('.stack-rev-card');
        if (!card) return;
        var body = card.querySelector('[data-review-body]');
        var tpl = card.querySelector('[data-review-orig]');
        if (!body || !tpl) return;
        var translated = body.innerHTML;
        var original = tpl.innerHTML;
        var showingOriginal = false;
        var showLabel = link.getAttribute('data-show') || 'Show original';
        var hideLabel = link.getAttribute('data-hide') || 'Show translation';
        link.addEventListener('click', function () {
          showingOriginal = !showingOriginal;
          body.innerHTML = showingOriginal ? original : translated;
          link.textContent = showingOriginal ? hideLabel : showLabel;
        });
      });
    };

    return StackReview;
  })();

  /* =====================================================================
     <stack-announcement> — announcement bar (standalone). Two modes:
       · info      → on mobile, rotates one item at a time (fade + slide-in
                     from the left). Desktop shows the full row via CSS.
       · countdown → a live days/hrs/mins/secs timer to a fixed date, which
                     optionally rolls forward ("repeat") instead of expiring.
     Progressive enhancement: without JS the info bar shows its first item and
     the countdown shows its server-rendered seed values.
     ===================================================================== */
  var StackAnnouncement = (function () {
    function StackAnnouncement() { return Reflect.construct(HTMLElement, [], StackAnnouncement); }
    StackAnnouncement.prototype = Object.create(HTMLElement.prototype);
    StackAnnouncement.prototype.constructor = StackAnnouncement;

    function pad(n) { return (n < 10 ? '0' : '') + n; }

    StackAnnouncement.prototype.connectedCallback = function () {
      if (this._init) return;
      this._init = true;
      if (this.getAttribute('data-mode') === 'countdown') {
        this._startCountdown();
      } else {
        this._startRotator();
      }
    };

    StackAnnouncement.prototype.disconnectedCallback = function () {
      if (this._cd) clearInterval(this._cd);
      if (this._rot) clearInterval(this._rot);
      if (this._mq && this._onMq) {
        if (this._mq.removeEventListener) this._mq.removeEventListener('change', this._onMq);
        else if (this._mq.removeListener) this._mq.removeListener(this._onMq);
      }
    };

    /* ---- Info rotator (mobile only) ---- */
    StackAnnouncement.prototype._startRotator = function () {
      var wrap = this.querySelector('[data-ann-info]');
      if (!wrap) return;
      this._items = Array.prototype.slice.call(wrap.querySelectorAll('.stack-ann__item'));
      if (this._items.length < 2) return; // nothing to rotate
      this._wrap = wrap;
      this._delay = parseInt(this.getAttribute('data-rotate'), 10) || 3000;
      this._idx = 0;

      var self = this;
      // Matches the info-rotator breakpoint in assets/stack.css.
      this._mq = window.matchMedia('(max-width: 900px)');
      this._onMq = function () { self._applyRotator(); };
      if (this._mq.addEventListener) this._mq.addEventListener('change', this._onMq);
      else if (this._mq.addListener) this._mq.addListener(this._onMq);
      this._applyRotator();
    };

    StackAnnouncement.prototype._applyRotator = function () {
      var on = this._mq.matches;
      if (on && !this._rot) {
        // Take control: mark ready (CSS switches to active-only) + show first.
        this._wrap.classList.add('is-ready');
        this._idx = 0;
        this._show(0);
        var self = this;
        this._rot = setInterval(function () { self._tick(); }, this._delay);
      } else if (!on && this._rot) {
        clearInterval(this._rot);
        this._rot = null;
        this._wrap.classList.remove('is-ready');
        for (var i = 0; i < this._items.length; i++) this._items[i].classList.remove('is-active');
      }
    };

    StackAnnouncement.prototype._show = function (i) {
      for (var k = 0; k < this._items.length; k++) {
        this._items[k].classList.toggle('is-active', k === i);
      }
    };

    StackAnnouncement.prototype._tick = function () {
      this._idx = (this._idx + 1) % this._items.length;
      this._show(this._idx);
    };

    /* ---- Countdown ---- */
    StackAnnouncement.prototype._startCountdown = function () {
      var endAttr = parseInt(this.getAttribute('data-end'), 10);
      if (!endAttr) return; // no / unparseable date — leave the seed values
      this._end = endAttr * 1000;
      this._repeat = this.getAttribute('data-repeat') === 'true';
      this._repeatMs = (parseInt(this.getAttribute('data-repeat-secs'), 10) || 0) * 1000;

      this._els = {
        days: this.querySelector('[data-cd-days]'),
        hours: this.querySelector('[data-cd-hours]'),
        mins: this.querySelector('[data-cd-mins]'),
        secs: this.querySelector('[data-cd-secs]')
      };
      this._timer = this.querySelector('[data-ann-timer]');
      this._cdText = this.querySelector('.stack-ann__cd-text');
      this._expired = this.querySelector('[data-ann-expired]');

      var self = this;
      this._render();
      this._cd = setInterval(function () { self._render(); }, 1000);
    };

    StackAnnouncement.prototype._render = function () {
      var now = Date.now();
      var end = this._end;

      if (now >= end) {
        if (this._repeat && this._repeatMs > 0) {
          // Roll the target forward whole periods until it is in the future.
          var periods = Math.ceil((now - end + 1) / this._repeatMs);
          this._end = end + periods * this._repeatMs;
          end = this._end;
        } else {
          this._expire();
          return;
        }
      }

      var total = Math.floor((end - now) / 1000);
      var days = Math.floor(total / 86400);
      var hours = Math.floor((total % 86400) / 3600);
      var mins = Math.floor((total % 3600) / 60);
      var secs = total % 60;

      if (this._els.days) this._els.days.textContent = pad(days);
      if (this._els.hours) this._els.hours.textContent = pad(hours);
      if (this._els.mins) this._els.mins.textContent = pad(mins);
      if (this._els.secs) this._els.secs.textContent = pad(secs);
    };

    StackAnnouncement.prototype._expire = function () {
      if (this._cd) { clearInterval(this._cd); this._cd = null; }
      // Zero the timer so it never shows a stale value.
      if (this._els.days) this._els.days.textContent = '00';
      if (this._els.hours) this._els.hours.textContent = '00';
      if (this._els.mins) this._els.mins.textContent = '00';
      if (this._els.secs) this._els.secs.textContent = '00';

      if (this._expired) {
        if (this._timer) this._timer.hidden = true;
        if (this._cdText) this._cdText.hidden = true;
        this._expired.hidden = false;
      } else {
        // No message set — remove the whole bar.
        this.classList.add('is-expired');
      }
    };

    return StackAnnouncement;
  })();

  /* =====================================================================
     <stack-compare> — comparison table. Owns the custom horizontal
     scrollbar: a draggable thumb synced to the table's overflow scroll.
     Without JS the table still scrolls natively (the bar just stays hidden).
     ===================================================================== */
  var StackCompare = (function () {
    function StackCompare() { return Reflect.construct(HTMLElement, [], StackCompare); }
    StackCompare.prototype = Object.create(HTMLElement.prototype);
    StackCompare.prototype.constructor = StackCompare;

    StackCompare.prototype.connectedCallback = function () {
      if (this._init) return;
      this._init = true;
      this.scroll = this.querySelector('[data-cmp-scroll]');
      this.bar = this.querySelector('[data-cmp-bar]');
      this.thumb = this.querySelector('[data-cmp-thumb]');
      if (!this.scroll || !this.bar || !this.thumb) return;
      var self = this;

      this._onScroll = function () {
        if (self._raf) return;
        self._raf = requestAnimationFrame(function () { self._raf = 0; self._update(); });
      };
      this.scroll.addEventListener('scroll', this._onScroll, { passive: true });

      this._onDown = function (e) { self._startDrag(e); };
      this.thumb.addEventListener('pointerdown', this._onDown);
      this._onBar = function (e) { if (e.target !== self.thumb) self._jump(e); };
      this.bar.addEventListener('pointerdown', this._onBar);

      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(function () { self._update(); });
        this._ro.observe(this.scroll);
        if (this.scroll.firstElementChild) this._ro.observe(this.scroll.firstElementChild);
      } else {
        this._onResize = function () { self._update(); };
        window.addEventListener('resize', this._onResize);
      }
      if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
        document.fonts.ready.then(function () { self._update(); });
      }
      this._update();
    };

    StackCompare.prototype.disconnectedCallback = function () {
      if (this._ro) this._ro.disconnect();
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      this._endDrag();
    };

    StackCompare.prototype._metrics = function () {
      var sc = this.scroll;
      var max = sc.scrollWidth - sc.clientWidth;
      var trackW = this.bar.clientWidth;
      var ratio = sc.scrollWidth > 0 ? sc.clientWidth / sc.scrollWidth : 1;
      var thumbW = Math.max(28, Math.min(trackW, Math.round(trackW * ratio)));
      return { max: max, trackW: trackW, thumbW: thumbW };
    };

    StackCompare.prototype._update = function () {
      var m = this._metrics();
      var scrollable = m.max > 2;
      this.classList.toggle('is-scrollable', scrollable);
      if (!scrollable) return;
      var travel = m.trackW - m.thumbW;
      var x = m.max > 0 ? (this.scroll.scrollLeft / m.max) * travel : 0;
      this.thumb.style.width = m.thumbW + 'px';
      this.thumb.style.transform = 'translateX(' + Math.round(x) + 'px)';
    };

    StackCompare.prototype._startDrag = function (e) {
      var self = this;
      var m = this._metrics();
      var travel = m.trackW - m.thumbW;
      if (travel <= 0) return;
      var startX = e.clientX;
      var startScroll = this.scroll.scrollLeft;
      this.thumb.classList.add('is-dragging');
      try { this.thumb.setPointerCapture(e.pointerId); } catch (err) {}

      this._move = function (ev) {
        var frac = (ev.clientX - startX) / travel;
        self.scroll.scrollLeft = startScroll + frac * m.max;
      };
      this._up = function (ev) { self._endDrag(ev); };
      this.thumb.addEventListener('pointermove', this._move);
      this.thumb.addEventListener('pointerup', this._up);
      this.thumb.addEventListener('pointercancel', this._up);
      e.preventDefault();
    };

    StackCompare.prototype._endDrag = function (ev) {
      if (this._move && this.thumb) { this.thumb.removeEventListener('pointermove', this._move); }
      if (this._up && this.thumb) {
        this.thumb.removeEventListener('pointerup', this._up);
        this.thumb.removeEventListener('pointercancel', this._up);
      }
      this._move = null;
      this._up = null;
      if (this.thumb) {
        this.thumb.classList.remove('is-dragging');
        if (ev && ev.pointerId != null) { try { this.thumb.releasePointerCapture(ev.pointerId); } catch (err) {} }
      }
    };

    StackCompare.prototype._jump = function (e) {
      var m = this._metrics();
      var travel = m.trackW - m.thumbW;
      if (travel <= 0) return;
      var rect = this.bar.getBoundingClientRect();
      var frac = Math.max(0, Math.min(1, (e.clientX - rect.left - m.thumbW / 2) / travel));
      this.scroll.scrollTo({ left: frac * m.max, behavior: 'smooth' });
    };

    return StackCompare;
  })();

  /* =====================================================================
     <stack-cards> — card slider. Owns arrow nav, autoplay (looping, paused
     on hover/focus/drag and when the tab is hidden) and per-card video sound
     toggle. Without JS it's a native swipeable snap-scroller.
     ===================================================================== */
  var StackCards = (function () {
    function StackCards() { return Reflect.construct(HTMLElement, [], StackCards); }
    StackCards.prototype = Object.create(HTMLElement.prototype);
    StackCards.prototype.constructor = StackCards;

    StackCards.prototype.connectedCallback = function () {
      if (this._init) return;
      this._init = true;
      this.track = this.querySelector('[data-cards-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-cards-prev]');
      this.next = this.querySelector('[data-cards-next]');
      this.nav = this.querySelector('.stack-cards__nav');
      this._reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._autoplay = this.getAttribute('data-autoplay') === 'true' && !this._reduce;
      this._speed = parseInt(this.getAttribute('data-speed'), 10) || 4000;

      var self = this;
      if (this.prev) this.prev.addEventListener('click', function () { self._nudge(-1); });
      if (this.next) this.next.addEventListener('click', function () { self._nudge(1); });

      this._onScroll = function () {
        if (self._raf) return;
        self._raf = requestAnimationFrame(function () { self._raf = 0; self._update(); });
      };
      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      // Sound toggle for video cards.
      this._mutes = [];
      var buttons = this.querySelectorAll('[data-card-mute]');
      for (var i = 0; i < buttons.length; i++) {
        (function (btn) {
          self._mutes.push(btn);
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            self._toggleSound(btn);
          });
        })(buttons[i]);
      }

      // Autoplay + pause on interaction.
      if (this._autoplay) {
        this._start();
        this.addEventListener('pointerenter', function () { self._stop(); });
        this.addEventListener('pointerleave', function () { self._start(); });
        this.addEventListener('focusin', function () { self._stop(); });
        this.addEventListener('focusout', function () { self._start(); });
        this.track.addEventListener('pointerdown', function () { self._stop(); });
        this._onVis = function () { if (document.hidden) { self._stop(); } else { self._start(); } };
        document.addEventListener('visibilitychange', this._onVis);
      }

      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(function () { self._update(); });
        this._ro.observe(this.track);
      }
      this._update();
    };

    StackCards.prototype.disconnectedCallback = function () {
      this._stop();
      if (this._ro) this._ro.disconnect();
      if (this._onVis) document.removeEventListener('visibilitychange', this._onVis);
    };

    StackCards.prototype._step = function () {
      var card = this.track.querySelector('.stack-card');
      var gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return card ? card.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
    };

    StackCards.prototype._nudge = function (dir) {
      this.track.scrollBy({ left: dir * this._step(), behavior: 'smooth' });
    };

    StackCards.prototype._update = function () {
      var t = this.track;
      var max = t.scrollWidth - t.clientWidth;
      var noScroll = max <= 2;
      var x = t.scrollLeft;
      // When everything fits (e.g. all cards visible on desktop) there is nothing
      // to slide — hide the arrows entirely rather than showing them disabled.
      // Re-evaluated on resize via the ResizeObserver, so it stays responsive.
      if (this.nav) this.nav.style.display = noScroll ? 'none' : '';
      if (this.prev) this.prev.classList.toggle('is-off', noScroll || x <= 1);
      if (this.next) this.next.classList.toggle('is-off', noScroll || x >= max - 1);
    };

    StackCards.prototype._advance = function () {
      var t = this.track;
      var max = t.scrollWidth - t.clientWidth;
      if (max <= 2) return;
      if (t.scrollLeft >= max - 2) {
        t.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        this._nudge(1);
      }
    };

    StackCards.prototype._start = function () {
      if (!this._autoplay || this._timer) return;
      var self = this;
      this._timer = setInterval(function () { self._advance(); }, this._speed);
    };

    StackCards.prototype._stop = function () {
      if (this._timer) { clearInterval(this._timer); this._timer = null; }
    };

    StackCards.prototype._toggleSound = function (btn) {
      var video = btn.parentNode && btn.parentNode.querySelector('video');
      if (!video) return;
      var turnOn = video.muted;
      if (turnOn) {
        // Only one card plays sound at a time.
        for (var i = 0; i < this._mutes.length; i++) {
          var other = this._mutes[i];
          if (other === btn) continue;
          var ov = other.parentNode && other.parentNode.querySelector('video');
          if (ov) { ov.muted = true; }
          other.classList.remove('is-on');
        }
      }
      video.muted = !video.muted;
      btn.classList.toggle('is-on', !video.muted);
      if (!video.muted && video.paused) { var p = video.play(); if (p && p.catch) p.catch(function () {}); }
    };

    return StackCards;
  })();

  /* ---------------------------------------------------------------------
     Register everything
     --------------------------------------------------------------------- */
  /* =====================================================================
     <stack-size-guide> — fit hint row + a measurement-table modal.
     The modal is moved to <body> on first open so no ancestor stacking
     context can trap it under other content (same lesson as the video
     popup); its CSS is keyed to a per-instance class instead of the block
     id so the styles keep applying once it is detached.
     ===================================================================== */
  var StackSizeGuide = (function () {
    function El() { return Reflect.construct(HTMLElement, [], El); }
    El.prototype = Object.create(HTMLElement.prototype);
    El.prototype.constructor = El;

    El.prototype.connectedCallback = function () {
      if (this._init) return;
      this._init = true;
      var self = this;
      this._modal = this.querySelector('[data-sg-modal]');
      var trigger = this.querySelector('[data-sg-open]');
      if (trigger) {
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          self.open();
        });
      }
      if (!this._modal) return;
      this._onKey = function (e) { if (e.key === 'Escape') self.close(); };
      this._modal.addEventListener('click', function (e) {
        // Backdrop or the ✕ close; unit pills swap the visible table.
        if (e.target === self._modal || e.target.closest('[data-sg-close]')) { self.close(); return; }
        var unit = e.target.closest('[data-sg-unit]');
        if (unit) self._setUnit(unit.getAttribute('data-sg-unit'));
      });
    };

    El.prototype.disconnectedCallback = function () {
      this.close();
      // Modal lives on <body> once opened — remove it with the block.
      if (this._modal && this._modal.parentNode) this._modal.parentNode.removeChild(this._modal);
    };

    El.prototype.open = function () {
      if (!this._modal) return;
      if (this._hideTimer) { window.clearTimeout(this._hideTimer); this._hideTimer = null; }
      if (this._modal.parentNode !== document.body) document.body.appendChild(this._modal);
      var m = this._modal;
      m.hidden = false;
      // Double rAF so the hidden→shown state paints first and the fade/slide
      // transition actually runs.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { m.classList.add('is-open'); });
      });
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKey);
      var closeBtn = m.querySelector('[data-sg-close]');
      if (closeBtn && closeBtn.focus) closeBtn.focus({ preventScroll: true });
    };

    El.prototype.close = function () {
      if (!this._modal || this._modal.hidden) return;
      var m = this._modal;
      m.classList.remove('is-open');
      this._hideTimer = window.setTimeout(function () { m.hidden = true; }, 200);
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', this._onKey);
    };

    El.prototype._setUnit = function (key) {
      var pills = this._modal.querySelectorAll('[data-sg-unit]');
      var tables = this._modal.querySelectorAll('[data-sg-table]');
      var i;
      for (i = 0; i < pills.length; i++) {
        pills[i].classList.toggle('is-active', pills[i].getAttribute('data-sg-unit') === key);
        pills[i].setAttribute('aria-selected', pills[i].getAttribute('data-sg-unit') === key ? 'true' : 'false');
      }
      for (i = 0; i < tables.length; i++) {
        tables[i].hidden = tables[i].getAttribute('data-sg-table') !== key;
      }
    };

    return El;
  })();

  define('stack-product', StackProduct);
  define('stack-price', StackPrice);
  define('stack-title', StackTitle);
  define('stack-variant-picker', StackVariantPicker);
  define('stack-bundle', StackBundle);
  define('stack-quantity', StackQuantity);
  define('stack-stock-bar', StackStockBar);
  define('stack-sticky-atc', StackStickyAtc);
  define('stack-atc', StackAtc);
  define('stack-gallery', StackGallery);
  define('stack-video-carousel', StackVideoCarousel);
  define('stack-marquee', StackMarquee);
  define('stack-review', StackReview);
  define('stack-announcement', StackAnnouncement);
  define('stack-compare', StackCompare);
  /* =====================================================================
     <stack-shop-the-look> — "complete the look / get the fit"
     ---------------------------------------------------------------------
     A teaser opens a drawer (side / bottom / centre) listing 1–4 products,
     each with its own variant selection. Unavailable option values are struck
     out and recomputed against the current selection (so you can never pick a
     sold-out combination); the row image + price track the resolved variant.
     One button adds every ready product to the cart in a single /cart/add.js
     call. Self-contained: no controller, no host-theme cart dependency. The
     drawer is moved to <body> on open (see the size-guide note) so its CSS is
     keyed to a per-instance class.
     ===================================================================== */
  var StackShopTheLook = (function () {
    function El() { return Reflect.construct(HTMLElement, [], El); }
    El.prototype = Object.create(HTMLElement.prototype);
    El.prototype.constructor = El;

    function shopRoot() {
      var r = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      if (r.charAt(r.length - 1) !== '/') r += '/';
      return r;
    }

    El.prototype.connectedCallback = function () {
      if (this._init) return;
      this._init = true;
      var self = this;
      this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
      this.afterAdd = this.getAttribute('data-after-add') || 'confirm';
      this._drawer = this.querySelector('[data-stl-drawer]');
      // Both the image row and the button below it open the drawer.
      var openBtns = this.querySelectorAll('[data-stl-open]');
      Array.prototype.forEach.call(openBtns, function (btn) {
        btn.addEventListener('click', function (e) { e.preventDefault(); self.open(); });
      });
      if (!this._drawer) return;

      // The drawer gets moved to <body> on open, which detaches it from this
      // block and drops every inherited --stl-* / --section-* custom property
      // (radius, ratio, the button colour that inherits the page primary…). Copy
      // the *resolved* values onto the drawer now, while it's still a descendant,
      // so the drawer stays visually consistent with the block + page.
      this._syncTokens();

      this._list = this._drawer.querySelector('[data-stl-list]');
      this._addBtn = this._drawer.querySelector('[data-stl-add]');
      this._addText = this._drawer.querySelector('[data-stl-add-text]');
      this._success = this._drawer.querySelector('[data-stl-success]');
      this._cartLink = this._drawer.querySelector('[data-stl-cart]');
      if (this._cartLink) this._cartLink.href = shopRoot() + 'cart';

      // Parse each product row's embedded variant data.
      this._items = [];
      var rows = this._drawer.querySelectorAll('[data-stl-item]');
      Array.prototype.forEach.call(rows, function (row) {
        var script = row.querySelector('[data-stl-data]');
        var data;
        try { data = JSON.parse(script.textContent); } catch (e) { return; }
        var item = {
          row: row,
          data: data,
          sel: (data.selected || []).slice(),
          imageEl: row.querySelector('[data-stl-image]'),
          priceEl: row.querySelector('[data-stl-price]'),
          subEl: row.querySelector('[data-stl-subtitle]'),
          statusEl: row.querySelector('[data-stl-status]'),
          buttons: row.querySelectorAll('[data-stl-value]'),
          variant: null,
          ready: false
        };
        self._items.push(item);
        self._recompute(item);
      });

      // Delegated interactions.
      this._drawer.addEventListener('click', function (e) {
        var val = e.target.closest && e.target.closest('[data-stl-value]');
        if (val) { if (!val.disabled) self._pick(val); return; }
        if (e.target === self._drawer || (e.target.closest && e.target.closest('[data-stl-close]'))) { self.close(); return; }
        if (e.target.closest && e.target.closest('[data-stl-keep]')) { self.close(); return; }
        if (e.target.closest && e.target.closest('[data-stl-add]')) { self._addAll(); return; }
      });

      this._onKey = function (e) { if (e.key === 'Escape') self.close(); };
      this._updateAddButton();
    };

    // Copy the block's resolved design tokens onto the drawer element so they
    // survive the move to <body>. getComputedStyle resolves the var() chains
    // (e.g. --stl-btn → --stack-color-primary → the page's actual colour); if a
    // browser leaves a var() unresolved we skip it and the baked CSS fallback
    // on the drawer takes over.
    El.prototype._syncTokens = function () {
      if (!this._drawer) return;
      var cs = window.getComputedStyle(this);
      var names = ['--stl-radius', '--stl-ratio', '--stl-btn', '--stl-btn-text', '--stl-accent'];
      for (var i = 0; i < names.length; i++) {
        var v = cs.getPropertyValue(names[i]).trim();
        if (v && v.indexOf('var(') === -1) this._drawer.style.setProperty(names[i], v);
      }
    };

    El.prototype.disconnectedCallback = function () {
      this.close();
      if (this._drawer && this._drawer.parentNode) this._drawer.parentNode.removeChild(this._drawer);
    };

    El.prototype.open = function () {
      if (!this._drawer) return;
      if (this._hideTimer) { window.clearTimeout(this._hideTimer); this._hideTimer = null; }
      // Reset back to the list view (in case it was left on the success screen).
      if (this._success) this._success.hidden = true;
      if (this._drawer.parentNode !== document.body) document.body.appendChild(this._drawer);
      var d = this._drawer;
      d.hidden = false;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { d.classList.add('is-open'); });
      });
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKey);
      var close = d.querySelector('[data-stl-close]');
      if (close && close.focus) close.focus({ preventScroll: true });
    };

    El.prototype.close = function () {
      if (!this._drawer || this._drawer.hidden) return;
      var d = this._drawer;
      d.classList.remove('is-open');
      this._hideTimer = window.setTimeout(function () { d.hidden = true; }, 260);
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', this._onKey);
    };

    // A value at option `i` is selectable if some AVAILABLE variant carries it
    // while matching every OTHER currently-selected option.
    El.prototype._valueAvailable = function (item, i, value) {
      var vs = item.data.variants;
      for (var k = 0; k < vs.length; k++) {
        var v = vs[k];
        if (!v.available) continue;
        if (v.options[i] !== value) continue;
        var ok = true;
        for (var j = 0; j < item.sel.length; j++) {
          if (j === i) continue;
          if (item.sel[j] != null && v.options[j] !== item.sel[j]) { ok = false; break; }
        }
        if (ok) return true;
      }
      return false;
    };

    El.prototype._findVariant = function (item) {
      var vs = item.data.variants;
      for (var k = 0; k < vs.length; k++) {
        var v = vs[k], match = true;
        for (var j = 0; j < item.sel.length; j++) {
          if (v.options[j] !== item.sel[j]) { match = false; break; }
        }
        if (match) return v;
      }
      return null;
    };

    El.prototype._pick = function (btn) {
      var item = null, i;
      for (i = 0; i < this._items.length; i++) {
        if (this._items[i].row.contains(btn)) { item = this._items[i]; break; }
      }
      if (!item) return;
      var idx = parseInt(btn.getAttribute('data-option-index'), 10);
      item.sel[idx] = btn.getAttribute('data-value');
      this._recompute(item);
      this._updateAddButton();
    };

    El.prototype._recompute = function (item) {
      // Reflect availability + pressed state on every value button.
      Array.prototype.forEach.call(item.buttons, function (b) {
        var i = parseInt(b.getAttribute('data-option-index'), 10);
        var value = b.getAttribute('data-value');
        var avail = this._valueAvailable(item, i, value);
        b.disabled = !avail;
        b.classList.toggle('is-soldout', !avail);
        b.setAttribute('aria-pressed', item.sel[i] === value ? 'true' : 'false');
      }, this);

      var v = this._findVariant(item);
      item.variant = v;
      item.ready = !!(v && v.available);

      // Image + price + subtitle track the resolved (or best-known) variant.
      var img = (v && v.image) || item.data.featured;
      if (item.imageEl && item.imageEl.tagName === 'IMG' && img) item.imageEl.src = img;
      var price = v ? v.price : item.data.price;
      if (item.priceEl) item.priceEl.textContent = formatMoney(price, this.moneyFormat);
      if (item.subEl && v) item.subEl.textContent = v.options[0];

      // Status line: fully sold out, or an unavailable combination.
      var anyAvail = item.data.variants.some(function (x) { return x.available; });
      item.soldOut = !anyAvail;
      if (item.statusEl) {
        var msg = '';
        if (!anyAvail) msg = 'Sold out';
        else if (v && !v.available) msg = 'This combination is sold out';
        item.statusEl.textContent = msg;
        item.statusEl.hidden = !msg;
      }
      item.row.classList.toggle('is-unavailable', !anyAvail);
    };

    El.prototype._readyItems = function () {
      return this._items.filter(function (it) { return it.ready; });
    };

    El.prototype._updateAddButton = function () {
      if (!this._addBtn) return;
      var ready = this._readyItems();
      var count = ready.length;
      var total = ready.reduce(function (sum, it) { return sum + it.variant.price; }, 0);
      this._addBtn.disabled = count === 0;
      if (this._addText) {
        if (count === 0) {
          this._addText.textContent = this._addBtn.getAttribute('data-empty-label') || 'Select your sizes';
        } else {
          var label = this._addBtn.getAttribute('data-add-label') || 'Add to cart';
          var noun = count === 1 ? 'item' : 'items';
          this._addText.textContent = label + ' · ' + count + ' ' + noun + ' · ' + formatMoney(total, this.moneyFormat);
        }
      }
    };

    El.prototype._addAll = function () {
      var self = this;
      var ready = this._readyItems();
      if (!ready.length || this._adding) return;
      this._adding = true;
      if (this._addText) this._addText.textContent = 'Adding…';
      this._addBtn.disabled = true;

      var items = ready.map(function (it) { return { id: it.variant.id, quantity: 1 }; });
      fetch(shopRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (e) { throw e; });
          return res.json();
        })
        .then(function () {
          self._adding = false;
          // Let any host-theme cart UI that listens refresh itself.
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          try { document.dispatchEvent(new CustomEvent('cart:build')); } catch (e) {}
          if (self.afterAdd === 'cart') { window.location = shopRoot() + 'cart'; return; }
          if (self.afterAdd === 'checkout') { window.location = shopRoot() + 'checkout'; return; }
          if (self.afterAdd === 'drawer') {
            // Close this panel and open the host theme's cart drawer (updated +
            // opened by the shared cart pipeline). No in-drawer confirmation.
            self.close();
            notifyCartUpdate('add', true);
            return;
          }
          // confirm: refresh the cart quietly + show the in-drawer success panel.
          // Still an 'add', so the drawer's auto-open is suppressed for the
          // dispatch — the success panel IS the confirmation here.
          notifyCartUpdate('add', false);
          if (self._success) self._success.hidden = false;
        })
        .catch(function () {
          self._adding = false;
          self._updateAddButton();
          if (self._addText) self._addText.textContent = 'Something went wrong — try again';
        });
    };

    return El;
  })();

  /* =====================================================================
     <stack-upsell> — buy-box cross-sell (blocks/stack-upsell.liquid)

     Two modes (data-mode):
       with_main — selected rows just track their own selected state; the
         actual add happens in StackProduct._wireCartSubmit, which reads
         _items() from every sibling with_main upsell block on the SAME
         submit that adds the main product (one combined /cart/add.js call).
       instant — checking adds to the cart immediately; unchecking removes
         exactly one (cart.js line lookup → change.js by line key). Switching
         the variant dropdown while checked swaps old for new.
     ===================================================================== */
  var StackUpsell = (function () {
    function shopRoot() {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return root.charAt(root.length - 1) === '/' ? root : root + '/';
    }

    function El() { return Reflect.construct(HTMLElement, [], El); }
    El.prototype = Object.create(HTMLElement.prototype);
    El.prototype.constructor = El;

    El.prototype.connectedCallback = function () {
      if (this._init) return;
      this._init = true;
      var self = this;
      this._mode = this.getAttribute('data-mode') || 'with_main';
      this._fmt = this.getAttribute('data-money-format') || '${{amount}}';
      this._deal = this.getAttribute('data-deal') || '';
      this._openDrawer = this.getAttribute('data-open-drawer') === 'true';
      this._plus = this.getAttribute('data-show-plus') === 'true';
      this._showCompare = this.getAttribute('data-show-compare') === 'true';
      this._showSave = this.getAttribute('data-show-save') === 'true';
      this._rows = [];

      var items = this.querySelectorAll('[data-ups-item]');
      for (var i = 0; i < items.length; i++) {
        (function (el) {
          var dataEl = el.querySelector('[data-ups-data]');
          if (!dataEl) return;
          var data;
          try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
          var row = {
            el: el,
            variants: data.v || [],
            idx: parseInt(el.getAttribute('data-ups-current'), 10) || 0,
            input: el.querySelector('[data-ups-input]'),
            btn: el.querySelector('[data-ups-btn]'),
            btnLabel: el.querySelector('[data-ups-btn-label]'),
            select: el.querySelector('[data-ups-select]'),
            priceEl: el.querySelector('[data-ups-price]'),
            compareEl: el.querySelector('[data-ups-compare]'),
            saveEl: el.querySelector('[data-ups-save]'),
            savePctEl: el.querySelector('[data-ups-save-pct]'),
            imgEl: el.querySelector('[data-ups-img]'),
            selected: false
          };
          self._rows.push(row);
          if (row.select) row.select.addEventListener('change', function () { self._onVariant(row); });
          if (row.input) row.input.addEventListener('change', function () { self._onSelect(row, row.input.checked); });
          if (row.btn) row.btn.addEventListener('click', function () { self._onButton(row); });
        })(items[i]);
      }
    };

    El.prototype._variant = function (row) {
      return row.variants[row.idx] || null;
    };

    /* Build a /cart/add.js line for a variant, stamping the block's encoded
       discount as the __stack_upsell attribute so the discount Function applies it. */
    El.prototype._mkItem = function (v) {
      var it = { id: v.id, quantity: 1 };
      if (this._deal) it.properties = { __stack_upsell: this._deal };
      return it;
    };

    /* Selected rows with an available variant → /cart/add.js items. In
       with_main mode this is read directly by StackProduct._wireCartSubmit. */
    El.prototype._items = function () {
      var out = [];
      for (var i = 0; i < this._rows.length; i++) {
        var row = this._rows[i];
        var v = this._variant(row);
        if (row.selected && v && v.a) out.push(this._mkItem(v));
      }
      return out;
    };

    El.prototype._onVariant = function (row) {
      var old = this._variant(row);
      row.idx = parseInt(row.select.value, 10) || 0;
      var v = this._variant(row);
      this._render(row);
      // Instant mode with the row already in the cart: swap old for new.
      if (this._mode === 'instant' && row.selected && old && v && old.id !== v.id) {
        var self = this;
        this._remove(old.id).then(function () { if (v.a) self._add([self._mkItem(v)]); });
      }
    };

    El.prototype._onSelect = function (row, on) {
      row.selected = on;
      row.el.classList.toggle('is-selected', on);
      if (this._mode !== 'instant') return;
      var v = this._variant(row);
      if (!v) return;
      if (on) { if (v.a) this._add([this._mkItem(v)], this._openDrawer); }
      else { this._remove(v.id); }
    };

    El.prototype._onButton = function (row) {
      var v = this._variant(row);
      if (!v || !v.a) return;
      var self = this;
      if (this._mode === 'with_main') {
        row.selected = !row.selected;
        row.el.classList.toggle('is-selected', row.selected);
        row.btn.setAttribute('aria-pressed', String(row.selected));
        if (row.btnLabel) {
          row.btnLabel.textContent = row.selected
            ? (row.btnLabel.getAttribute('data-label-added') || 'Added')
            : (row.btnLabel.getAttribute('data-label-add') || 'Add');
        }
        return;
      }
      // Instant: add one, flash the added state, then allow adding another.
      if (row._busy) return;
      row._busy = true;
      row.btn.setAttribute('aria-pressed', 'true');
      if (row.btnLabel) row.btnLabel.textContent = row.btnLabel.getAttribute('data-label-added') || 'Added';
      this._add([this._mkItem(v)], this._openDrawer).then(done, done);
      function done() {
        setTimeout(function () {
          row._busy = false;
          row.btn.setAttribute('aria-pressed', 'false');
          if (row.btnLabel) row.btnLabel.textContent = row.btnLabel.getAttribute('data-label-add') || 'Add';
        }, 1400);
      }
    };

    El.prototype._render = function (row) {
      var v = this._variant(row);
      if (!v) return;
      if (row.priceEl) row.priceEl.textContent = (this._plus ? '+' : '') + formatMoney(v.p, this._fmt);
      var onSale = v.c && v.c > v.p;
      if (row.compareEl) {
        row.compareEl.hidden = !(this._showCompare && onSale);
        if (onSale) row.compareEl.textContent = formatMoney(v.c, this._fmt);
      }
      if (row.saveEl) {
        row.saveEl.hidden = !(this._showSave && onSale);
        if (onSale && row.savePctEl) row.savePctEl.textContent = String(Math.round(((v.c - v.p) * 100) / v.c));
      }
      if (row.imgEl && v.img) row.imgEl.src = v.img;
      var dis = !v.a;
      if (row.input) {
        row.input.disabled = dis;
        if (dis && row.input.checked) { row.input.checked = false; this._onSelect(row, false); }
      }
      if (row.btn) row.btn.disabled = dis;
    };

    El.prototype._add = function (items, openDrawer) {
      return stackCartAdd(items).then(function (data) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        notifyCartUpdate('add', openDrawer === true);
        return data;
      });
    };

    /* Remove exactly ONE of the given variant: read the cart, find its line,
       decrement by line key (never wipes quantity the shopper added elsewhere). */
    El.prototype._remove = function (variantId) {
      return fetch(shopRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          var line = null;
          var items = (cart && cart.items) || [];
          for (var i = 0; i < items.length; i++) {
            if (items[i].variant_id === variantId) { line = items[i]; break; }
          }
          if (!line) return null;
          var left = Math.max(0, line.quantity - 1);
          return fetch(shopRoot() + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: left })
          })
            .then(function (res) { return res.json(); })
            .then(function (data) {
              document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
              // Never 'add' — that is what made unchecking an upsell pop the
              // cart drawer open on Horizon 4.x.
              notifyCartUpdate(left === 0 ? 'remove' : 'update', false);
              return data;
            });
        });
    };

    return El;
  })();

  define('stack-cards', StackCards);
  define('stack-size-guide', StackSizeGuide);
  define('stack-shop-the-look', StackShopTheLook);
  define('stack-upsell', StackUpsell);

  // Host-theme cart bridge: load the per-theme adapter right away so it is
  // listening before the first AJAX add.
  loadStackTheme();
})();
